import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toPng, toBlob } from 'html-to-image';
import { supabase } from '../lib/supabase';
import { EmptyState } from '../ui/EmptyState';
import { timeAgo } from '../lib/timeAgo';
import { loadStampHolders } from '../lib/stampHolders';
import { loadImageGen, type ImgDraft } from './ImageStudio';
import { STAMP_DEFINITIONS } from '../data/challengeStamps';
import {
  fetchCityRankedVenues,
  fetchRatedCities,
  fetchVenueRankContext,
  searchRatedVenues,
  venueDisplayName,
  type GrowthCity,
  type GrowthVenue,
} from '../lib/growth';

/**
 * "Make anything": brief → (clarifying questions) → studio-generate edge function →
 * one self-contained HTML asset in the brand system, rendered in a sandboxed
 * same-origin iframe (no scripts), editable in place (designMode), refinable,
 * exportable as PNG at true pixel size, and saveable to the prod studio_assets library.
 *
 * Data attach: live city rankings, venue stats (price/peak/mood when present), app-wide
 * counts, and APPROVED community photos (venues.approved_venue_photo_url + venue_photos
 * where approved) — Google/FSQ imagery is never offered to the generator.
 *
 * Drafts: the latest generated state autosaves to localStorage and restores on reopen;
 * the library (prod table) is the durable save.
 */

type Preset = { label: string; w: number; h: number };
const PRESETS: Preset[] = [
  { label: 'Story 1080×1920', w: 1080, h: 1920 },
  { label: 'Square 1080', w: 1080, h: 1080 },
  { label: 'Portrait 1080×1350', w: 1080, h: 1350 },
  { label: 'Wide 1600×900', w: 1600, h: 900 },
  { label: 'App Store 1290×2796', w: 1290, h: 2796 },
  { label: 'Table tent 1200×1800', w: 1200, h: 1800 },
  { label: 'Poster 8.5×11″ · 300dpi', w: 2550, h: 3300 },
  { label: 'Poster 18×24″ · 150dpi', w: 2700, h: 3600 },
];

/** Multi-slide assets arrive as root divs separated by this marker (edge fn hard rule 9). */
const SLIDE_MARKER = /<!--\s*SLIDE\s*-->/i;
/** "Show me options" responses arrive as alternatives separated by this (edge fn hard rule 10). */
const VARIANT_MARKER = /<!--\s*VARIANT\s*-->/i;
const VARIANT_SPLIT = /<!--\s*VARIANT\s*-->/gi;
const STYLESHEET_LINE = '<link rel="stylesheet" href="/growth/clash-fonts.css">';

function splitSlides(full: string): string[] {
  return full.split(SLIDE_MARKER).map((s) => s.trim()).filter(Boolean);
}

/** Each slide renders in its own iframe, so each needs the font stylesheet (it ships once, on
 * slide 1) AND a dark base: without it the iframe paints a white default document for a frame
 * when flipping slides (the "white outline" flash, Evan 2026-10-16). */
const DOC_BASE_STYLE = '<style data-mk-doc-base>html,body{margin:0;background:#08090A}</style>';
function ensureStylesheet(slide: string): string {
  const withCss = /clash-fonts\.css/.test(slide) ? slide : `${STYLESHEET_LINE}\n${slide}`;
  return /data-mk-doc-base/.test(withCss) ? withCss : `${DOC_BASE_STYLE}\n${withCss}`;
}

const DRAFT_KEY = 'admin:studio:make:draft'; // legacy single-draft key, migrated once
const DRAFTS_KEY = 'admin:studio:make:drafts:v1';

/** One Make-Anything draft/project, exactly the payload the old single autosave carried. */
type MakeDraftState = {
  prompt?: string;
  html?: string;
  width?: number;
  height?: number;
  assetId?: string | null;
  at?: number;
  versions?: { html: string; at: number; note: string }[];
  instructions?: string[];
  usedAnswers?: { question: string; answer: string }[];
  photoOptions?: { venue: string; urls: string[] }[];
  venueOptions?: VenueOpt[];
  backdrops?: { url: string; prompt: string; kind?: 'backdrop' | 'image' }[];
  exportLabel?: string;
  label?: string;
  manualEdits?: string[];
};
type MakeDraftsStore = { current: string; order: string[]; drafts: Record<string, MakeDraftState> };

function draftLabelOf(d: MakeDraftState | undefined): string {
  if (!d) return 'untitled';
  return (
    (d.label ?? '').trim() ||
    (d.exportLabel ?? '').trim() ||
    (d.prompt ?? '').trim().slice(0, 28) ||
    'untitled'
  );
}

function readDraftsStore(): MakeDraftsStore | null {
  try {
    const raw = window.localStorage.getItem(DRAFTS_KEY);
    if (!raw) return null;
    const st = JSON.parse(raw) as MakeDraftsStore;
    if (!st || !Array.isArray(st.order) || typeof st.drafts !== 'object') return null;
    return st;
  } catch {
    return null;
  }
}

function writeDraftsStore(st: MakeDraftsStore): void {
  try {
    window.localStorage.setItem(DRAFTS_KEY, JSON.stringify(st));
  } catch {
    /* storage full — drafts are best-effort */
  }
}

type SavedAsset = {
  id: string;
  title: string;
  prompt: string;
  html: string;
  width: number;
  height: number;
  updated_at: string;
  created_by?: string | null;
};

type Question = { id: string; question: string; options: string[] };
type UsageMeter = { month_cost_usd: number; monthly_cap_usd: number; mine_cost_usd?: number };
type PlanEntities = { venues?: string[]; cities?: string[]; campuses?: string[] };

type StudioResponse = {
  html?: string;
  questions?: Question[];
  entities?: PlanEntities;
  error?: string;
  truncated?: boolean;
  usage?: UsageMeter & { cost_usd: number };
};

async function callStudio(body: Record<string, unknown>): Promise<
  { ok: true; data: StudioResponse } | { ok: false; error: string }
> {
  const { data, error } = await supabase.functions.invoke('studio-generate', { body });
  if (error) {
    let msg = error.message || 'Request failed';
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as { error?: string };
        if (parsed?.error) msg = parsed.error;
      } catch {
        /* keep default message */
      }
    }
    return { ok: false, error: msg };
  }
  return { ok: true, data: (data ?? {}) as StudioResponse };
}

/**
 * The working animation (Evan 2026-10-09: "any time the ai is working, i want where the output
 * is to turn into a cool repetitive animation"). A canvas-shaped stage at the exact size the
 * asset will occupy: breathing signature wash, a diagonal light sweep, and the violet mark
 * morphing square ↔ circle — all CSS keyframes, no timers, loops until the result lands.
 */
function GeneratingCanvas({ w, h, phase }: { w: number; h: number; phase: 'plan' | 'generate' | 'refine' }) {
  const label =
    phase === 'plan' ? 'Reading the brief' : phase === 'refine' ? 'Revising' : 'Designing';
  return (
    <div className="admin-card inline-block !p-4">
      <style>{`
        @keyframes mk-breathe { 0%,100%{opacity:.45;transform:scale(1)} 50%{opacity:1;transform:scale(1.07)} }
        @keyframes mk-morph {
          0%   {transform:rotate(0deg) scale(1);   border-radius:28%}
          25%  {transform:rotate(90deg) scale(.8); border-radius:50%}
          50%  {transform:rotate(180deg) scale(1); border-radius:28%}
          75%  {transform:rotate(270deg) scale(.8);border-radius:50%}
          100% {transform:rotate(360deg) scale(1); border-radius:28%}
        }
        @keyframes mk-sweep { 0%{transform:translateX(-140%) skewX(-18deg)} 100%{transform:translateX(340%) skewX(-18deg)} }
        @keyframes mk-dots { 0%{content:''} 25%{content:'.'} 50%{content:'..'} 75%{content:'...'} 100%{content:''} }
        .mk-dots::after { display:inline-block; width:1.2em; text-align:left; content:''; animation: mk-dots 1.4s steps(1) infinite; }
      `}</style>
      <div
        className="relative overflow-hidden rounded-lg border border-borderStrong bg-[#08090A]"
        style={{ width: w, height: h }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(130% 55% at 50% -8%, rgba(139,92,246,0.30), transparent 62%)',
            animation: 'mk-breathe 2.6s ease-in-out infinite',
          }}
        />
        <div
          className="absolute inset-y-0 left-0 w-1/3"
          style={{
            background:
              'linear-gradient(100deg, transparent, rgba(139,92,246,0.10), rgba(255,255,255,0.055), transparent)',
            animation: 'mk-sweep 2.3s ease-in-out infinite',
          }}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
          <div
            style={{
              width: 46,
              height: 46,
              background: '#8B5CF6',
              boxShadow: '0 0 46px rgba(139,92,246,0.55), 0 0 0 10px rgba(139,92,246,0.12)',
              animation: 'mk-morph 2.8s cubic-bezier(.65,.05,.35,.95) infinite',
            }}
          />
          <div className="mk-dots text-[13px] font-medium text-[#b79cff]">{label}</div>
        </div>
      </div>
      <p className="mt-2 text-[10.5px] tabular-nums text-mutedDim">
        {phase === 'generate' ? 'Usually ~30s — big carousels take about a minute.' : 'A few seconds.'}
      </p>
    </div>
  );
}

/** One selectable venue anywhere in the studio — id present whenever the source knew it. */
type VenueOpt = {
  id?: string | null;
  name: string;
  score: number | null;
  raters: number | null;
  photo: string | null;
  city?: string | null;
  cityId?: string | null;
  /** Approved photos available (hero + gallery) — filled for city lists. */
  photos?: number | null;
};

/**
 * Our own dropdown (Evan 2026-10-11: "make the dropdown not just the iOS default") — a dark
 * panel listing each venue with score, rater count and photo count. Closes on pick, outside
 * click or Escape.
 */
function VenueDropdown({
  list,
  value,
  onPick,
}: {
  list: VenueOpt[];
  value: string | null;
  onPick: (v: VenueOpt) => void;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg border border-border bg-surfaceElevated px-3 py-1.5 text-[12px] text-white hover:border-accent/50"
      >
        <span className="max-w-[260px] truncate">{value ?? 'choose a venue…'}</span>
        <span className="text-[10px] text-mutedDim">{open ? '▴' : '▾'}</span>
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div className="absolute left-0 z-50 mt-1 max-h-[340px] w-[420px] overflow-y-auto rounded-xl border border-borderStrong bg-surface p-1 shadow-2xl shadow-black/60">
            {list.map((v) => {
              const active = v.name === value;
              return (
                <button
                  key={v.name}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    if (!active) onPick(v);
                  }}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                    active ? 'bg-accent/15' : 'hover:bg-white/[0.05]'
                  }`}
                >
                  <span className={`min-w-0 flex-1 truncate text-[12.5px] ${active ? 'text-[#b79cff]' : 'text-white'}`}>
                    {v.name}
                  </span>
                  <span className="shrink-0 font-display text-[12.5px] font-semibold tabular-nums text-[#b79cff]">
                    {v.score != null ? v.score.toFixed(1) : '—'}
                  </span>
                  <span className="w-[64px] shrink-0 text-right text-[10.5px] tabular-nums text-mutedDim">
                    {v.raters ?? '?'} raters
                  </span>
                  <span className="w-[62px] shrink-0 text-right text-[10.5px] tabular-nums text-mutedDim">
                    {v.photos != null ? `${v.photos} photo${v.photos === 1 ? '' : 's'}` : '—'}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}

/** "show me options", "4 options", "a few options" → how many takes to request (cap 4). */
function variantsFromText(s: string): number | undefined {
  const m = s.match(/\b(\d+)\s*(?:design\s*)?options?\b/i);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n >= 2) return Math.min(4, n);
  }
  if (/\b(?:show (?:me )?|see |give me |want )(?:some |a few )?options\b|\ba few options\b|\bsome options\b/i.test(s)) {
    return 3;
  }
  return undefined;
}

// Always on (Evan 2026-10-09: "it should automatically have permission"): approved photos
// and app-wide stats are the model's to use, not a checkbox. Module-level on purpose — they
// are policy, not state, and must not appear in hook dependency arrays.
const attachPhotos = true;
const attachStats = true;

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The Clash Display @font-face rules (data-URI sources) handed EXPLICITLY to html-to-image.
 * Left to its own devices it walks the iframe document's styleSheets to find webfonts and
 * silently comes up empty — which is why exports fell back to system fonts (Evan 2026-10-16,
 * "the fonts dont all seem to be exporting right").
 */
let clashFontCssPromise: Promise<string> | null = null;
function getClashFontCss(): Promise<string> {
  if (!clashFontCssPromise) {
    clashFontCssPromise = fetch('/growth/clash-fonts.css')
      .then((r) => (r.ok ? r.text() : ''))
      .catch(() => '');
  }
  return clashFontCssPromise;
}

function slugify(s: string): string {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'asset';
}

export function MakeAnythingStudio() {
  const [prompt, setPrompt] = useState('');
  const [preset, setPreset] = useState<Preset>(PRESETS[0]);
  const [customW, setCustomW] = useState('');
  const [customH, setCustomH] = useState('');

  const [attachCity, setAttachCity] = useState(false);
  const [cities, setCities] = useState<GrowthCity[]>([]);
  const [cityId, setCityId] = useState('');
  const [attachVenue, setAttachVenue] = useState(false);
  const [venue, setVenue] = useState<GrowthVenue | null>(null);
  const [askFirst, setAskFirst] = useState(true);
  // Auto-attached from the brief itself (plan-call entity extraction) — no checkboxes needed.
  // extraVenues = venues named beyond the first (head-to-heads, multi-venue carousels).
  const [extraVenues, setExtraVenues] = useState<GrowthVenue[]>([]);
  const [campusName, setCampusName] = useState<string | null>(null);
  const [resolvedChips, setResolvedChips] = useState<string[]>([]);
  // Carousel: '1' = classic single asset; N>1 → numbered slide set. Remembered across
  // sessions (Evan 2026-10-09: "make sure card count is remembered session to session").
  const [slideCount, setSlideCount] = useState(() => {
    try {
      const n = parseInt(window.localStorage.getItem('admin:studio:make:slides') ?? '1', 10);
      return n >= 1 && n <= 10 ? n : 1;
    } catch {
      return 1;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem('admin:studio:make:slides', String(slideCount));
    } catch {
      /* best-effort */
    }
  }, [slideCount]);
  const [slideIdx, setSlideIdx] = useState(0);
  // "Show me options" (Evan 2026-10-09: "i tend to want to see options for how things look"):
  // alternative takes waiting to be picked, plus the bookkeeping to apply the pick correctly.
  // Approved photos available per resolved venue/city — rendered as a tap-to-swap strip
  // under the output (Evan 2026-10-09: "i want the options that i can choose from to show
  // underneath the output").
  const [photoOptions, setPhotoOptions] = useState<{ venue: string; urls: string[] }[]>([]);
  /** AI-generated imagery (Gemini via studio-image): kind 'backdrop' = abstract backgrounds
   * only; kind 'image' = freeform (Evan 2026-10-26: "any image not just backdrop"). */
  /** Imagery shared from the Images tab ("Use in asset" / Backdrop mode). This screen only
   * READS the shared store — the Images tab is its single writer; a draft snapshot can still
   * override via applyDraftState. */
  const [backdrops, setBackdrops] = useState<ImgDraft[]>(() => loadImageGen().backdrops ?? []);

  /** Candidate venues for the per-slide switcher — city top list + everything resolved. */
  const [venueOptions, setVenueOptions] = useState<
    VenueOpt[]
  >([]);
  /** Live per-city venue lists for the slide switcher: city_id → venues with an approved user
   * photo, most-rated first. Fetched on demand, cached for the session. */
  const [cityVenueLists, setCityVenueLists] = useState<
    Record<string, VenueOpt[]>
  >({});
  const [variantChoices, setVariantChoices] = useState<string[] | null>(null);
  const [variantOrigin, setVariantOrigin] = useState<
    | { kind: 'generate'; answers: { question: string; answer: string }[] }
    | { kind: 'refine'; instruction: string }
    | null
  >(null);

  const [questions, setQuestions] = useState<Question[] | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const [html, setHtml] = useState<string | null>(null);
  const [assetId, setAssetId] = useState<string | null>(null);
  /** Conversation history: every kept generation, with the instruction that produced it. */
  const [versions, setVersions] = useState<{ html: string; at: number; note: string }[]>([]);
  const [versionIdx, setVersionIdx] = useState(0);
  const [instructions, setInstructions] = useState<string[]>([]);
  const [usedAnswers, setUsedAnswers] = useState<{ question: string; answer: string }[]>([]);
  const [refine, setRefine] = useState('');
  /** Names the downloaded files (Evan 2026-10-16) — falls back to the brief's slug. */
  const [exportLabel, setExportLabel] = useState('');
  /** Export resolution multiplier — 1× was visibly soft on IG (Evan 2026-10-16), so 2× is the
   * default; persisted across sessions. */
  const [exportScale, setExportScale] = useState<1 | 2 | 3>(() => {
    try {
      const n = parseInt(window.localStorage.getItem('admin:studio:make:exportscale') ?? '2', 10);
      return n === 1 || n === 3 ? (n as 1 | 3) : 2;
    } catch {
      return 2;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem('admin:studio:make:exportscale', String(exportScale));
    } catch {
      /* best-effort */
    }
  }, [exportScale]);
  const [busy, setBusy] = useState<null | 'plan' | 'generate' | 'refine' | 'save' | 'export'>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The browse-the-library box was removed from this screen (Evan 2026-10-28 — the Image studio
  // card sits where it was); saves still land in the table and the list keeps assetId fresh.
  const [, setAssets] = useState<SavedAsset[]>([]);
  /** asset created_by → username, so the shared library says whose save is whose. */
  const [usage, setUsage] = useState<UsageMeter | null>(null);

  const frameRef = useRef<HTMLIFrameElement | null>(null);

  // ---- full-screen working mode + tap-to-restyle panel (Evan 2026-10-25: "full screen the
  // output I am working on", "click into text and get a popup panel like in other studios") ----
  const previewShellRef = useRef<HTMLDivElement | null>(null);
  const [isFull, setIsFull] = useState(false);
  const [fsBox, setFsBox] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  /** The element the popup panel is styling, plus its current values for the controls. */
  const [editSel, setEditSel] = useState<{
    kind: 'text' | 'img';
    left: number;
    top: number;
    fontSize: number;
    weight: number;
    color: string;
    align: string;
    radius: number;
    opacity: number;
  } | null>(null);
  const editTargetRef = useRef<HTMLElement | null>(null);
  /** Panel restyles, in words — folded into every refine/restructure call so the model treats
   * them as deliberate and never "fixes" them back. (The edited HTML itself is already the base
   * of every refine via currentHtml(); this makes the intent explicit too.) */
  const manualEditsRef = useRef<string[]>([]);
  // Multiple drafts/projects (Evan 2026-10-16) — chips above the brief; each slot carries the
  // full working state, switching never loses anything, the library stays the durable save.
  const [draftTabs, setDraftTabs] = useState<{ id: string; label: string }[]>([]);
  const [currentDraftId, setCurrentDraftId] = useState<string>(() => crypto.randomUUID());
  // Evan 2026-10-26: drafts are renamable — the chosen name wins over the derived label.
  const [draftName, setDraftName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);

  const width = Math.round(Number(customW)) > 0 ? Math.round(Number(customW)) : preset.w;
  const height = Math.round(Number(customH)) > 0 ? Math.round(Number(customH)) : preset.h;
  const previewW = 560;
  const scale = Math.min(1, previewW / width);
  /** In full screen the canvas scales to the window instead of the 560px column. */
  const effScale = isFull && fsBox.w > 0 ? Math.min(fsBox.w / width, fsBox.h / height, 1.6) : scale;

  const measureFsBox = useCallback(() => {
    setFsBox({ w: Math.max(240, window.innerWidth - 48), h: Math.max(240, window.innerHeight - 110) });
  }, []);

  const clearEditSel = useCallback(() => {
    const el = editTargetRef.current;
    if (el && el.isConnected) el.removeAttribute('data-mk-sel');
    editTargetRef.current = null;
    setEditSel(null);
  }, []);

  const enterFull = useCallback(() => {
    measureFsBox();
    setIsFull(true);
    const shell = previewShellRef.current;
    // Real fullscreen where the browser allows it (hides the chrome); the fixed overlay the
    // shell switches to is the whole fallback on iPhone Safari, which has no element fullscreen.
    if (shell?.requestFullscreen) void shell.requestFullscreen().catch(() => {});
  }, [measureFsBox]);

  const exitFull = useCallback(() => {
    setIsFull(false);
    clearEditSel();
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
  }, [clearEditSel]);

  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement) setIsFull(false);
      else measureFsBox();
    };
    const onResize = () => measureFsBox();
    const onKey = (e: KeyboardEvent) => {
      // Esc exits the overlay fallback (real fullscreen handles Esc itself via fullscreenchange).
      if (e.key === 'Escape' && !document.fullscreenElement) setIsFull(false);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onKey);
    };
  }, [measureFsBox]);

  /** Remember one note per element+property, newest wins, capped so the payload stays small. */
  const noteManualEdit = useCallback((el: HTMLElement, prop: string, note: string) => {
    const label =
      (el.tagName === 'IMG' ? el.getAttribute('alt') : el.textContent)?.trim().replace(/\s+/g, ' ').slice(0, 40) ||
      el.tagName.toLowerCase();
    const key = `"${label}" ${prop}`;
    const next = manualEditsRef.current.filter((s) => !s.startsWith(key));
    next.push(`${key} ${note}`);
    manualEditsRef.current = next.slice(-12);
  }, []);

  /** instructions + the manual-edit notes, the shape every studio call's history wants. */
  const historyWithManualEdits = useCallback(
    (list: string[]): string[] => [
      ...list,
      ...manualEditsRef.current.map((s) => `(my manual edit on the canvas — keep it) ${s}`),
    ],
    [],
  );

  const rgbToHex = (c: string): string => {
    const m = c.match(/rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/);
    if (!m) return c.startsWith('#') ? c : '#ffffff';
    return `#${[m[1], m[2], m[3]].map((n) => parseInt(n, 10).toString(16).padStart(2, '0')).join('')}`;
  };

  /** Click inside the asset → select the text block (or image) under the cursor and open the
   * panel next to it. Lives in a ref so the iframe's one listener always sees fresh state. */
  const pickEditTargetRef = useRef<(e: MouseEvent) => void>(() => {});
  pickEditTargetRef.current = (e: MouseEvent) => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    let el = e.target as HTMLElement | null;
    if (!el || el === doc.body || el === doc.documentElement) {
      clearEditSel();
      return;
    }
    const kind: 'text' | 'img' = el.tagName === 'IMG' ? 'img' : 'text';
    if (kind === 'text') {
      const hasOwnText = (n: HTMLElement) =>
        Array.from(n.childNodes).some((c) => c.nodeType === 3 && (c.textContent ?? '').trim());
      while (el && el !== doc.body && !hasOwnText(el)) el = el.parentElement;
      if (!el || el === doc.body) {
        clearEditSel();
        return;
      }
    }
    if (editTargetRef.current && editTargetRef.current !== el && editTargetRef.current.isConnected) {
      editTargetRef.current.removeAttribute('data-mk-sel');
    }
    if (!doc.querySelector('style[data-mk-ui]')) {
      const st = doc.createElement('style');
      st.setAttribute('data-mk-ui', '');
      st.textContent = '[data-mk-sel]{outline:2px dashed rgba(139,92,246,.85)!important;outline-offset:2px}';
      doc.head.appendChild(st);
    }
    el.setAttribute('data-mk-sel', '1');
    editTargetRef.current = el;
    const cs = doc.defaultView?.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    const s = isFull && fsBox.w > 0 ? Math.min(fsBox.w / width, fsBox.h / height, 1.6) : Math.min(1, previewW / width);
    const boxW = Math.round(width * s);
    const boxH = Math.round(height * s);
    setEditSel({
      kind,
      left: Math.max(8, Math.min(rect.left * s, boxW - 248)),
      top: Math.max(8, Math.min(rect.bottom * s + 10, boxH - 60)),
      fontSize: Math.round(parseFloat(cs?.fontSize ?? '16') || 16),
      weight: parseInt(cs?.fontWeight ?? '400', 10) || 400,
      color: rgbToHex(cs?.color ?? '#ffffff'),
      align: cs?.textAlign === 'center' ? 'center' : cs?.textAlign === 'right' ? 'right' : 'left',
      radius: Math.round(parseFloat(cs?.borderRadius ?? '0') || 0),
      opacity: Math.round((parseFloat(cs?.opacity ?? '1') || 1) * 100),
    });
  };

  /** Apply one style from the panel to the selected element — the iframe DOM is the document of
   * record, so currentHtml() picks this up for refine, save and export automatically. */
  const applyEditStyle = useCallback(
    (
      change: Partial<{ fontSize: number; weight: number; color: string; align: string; radius: number; opacity: number }>,
    ) => {
      const el = editTargetRef.current;
      if (!el) return;
      if (change.fontSize != null) {
        el.style.fontSize = `${change.fontSize}px`;
        noteManualEdit(el, 'font size', `set to ${change.fontSize}px`);
      }
      if (change.weight != null) {
        el.style.fontWeight = String(change.weight);
        noteManualEdit(el, 'font weight', `set to ${change.weight}`);
      }
      if (change.color != null) {
        el.style.color = change.color;
        noteManualEdit(el, 'color', `set to ${change.color}`);
      }
      if (change.align != null) {
        el.style.textAlign = change.align;
        noteManualEdit(el, 'alignment', `set to ${change.align}`);
      }
      if (change.radius != null) {
        el.style.borderRadius = `${change.radius}px`;
        noteManualEdit(el, 'corner radius', `set to ${change.radius}px`);
      }
      if (change.opacity != null) {
        el.style.opacity = String(change.opacity / 100);
        noteManualEdit(el, 'opacity', `set to ${change.opacity}%`);
      }
      setEditSel((s) => (s ? { ...s, ...change } : s));
    },
    [noteManualEdit],
  );

  const removeEditTarget = useCallback(() => {
    const el = editTargetRef.current;
    if (!el) return;
    noteManualEdit(el, '', 'removed from the design');
    el.remove();
    editTargetRef.current = null;
    setEditSel(null);
  }, [noteManualEdit]);

  /** Load a draft slot into every piece of working state ( null = a fresh empty project ). */
  const applyDraftState = useCallback((d: MakeDraftState | null) => {
    setPrompt(d?.prompt ?? '');
    setHtml(d?.html ?? null);
    setAssetId(d?.assetId ?? null);
    setCustomW(d?.width ? String(d.width) : '');
    setCustomH(d?.height ? String(d.height) : '');
    const vs = d?.html
      ? (Array.isArray(d.versions) && d.versions.length > 0
          ? d.versions
          : [{ html: d.html, at: d.at ?? Date.now(), note: 'Restored' }])
      : [];
    setVersions(vs);
    setVersionIdx(Math.max(0, vs.length - 1));
    setInstructions(Array.isArray(d?.instructions) ? d!.instructions! : []);
    setUsedAnswers(Array.isArray(d?.usedAnswers) ? d!.usedAnswers! : []);
    setPhotoOptions(Array.isArray(d?.photoOptions) ? d!.photoOptions! : []);
    setVenueOptions(Array.isArray(d?.venueOptions) ? d!.venueOptions! : []);
    setBackdrops(Array.isArray(d?.backdrops) ? d!.backdrops! : []);
    setExportLabel(typeof d?.exportLabel === 'string' ? d!.exportLabel! : '');
    setDraftName(typeof d?.label === 'string' ? d!.label! : '');
    manualEditsRef.current = Array.isArray(d?.manualEdits) ? d!.manualEdits! : [];
    // The Slides selector follows the draft being opened — without this a 7-slide draft showed
    // "Single" and offered a destructive restructure (Evan 2026-10-28: "stop defaulting to
    // single slide asset everytime i open").
    if (d?.html) setSlideCount(Math.min(10, Math.max(1, splitSlides(d.html).length)));
    editTargetRef.current = null;
    setEditSel(null);
    setSlideIdx(0);
    setQuestions(null);
    setRefine('');
    setVariantChoices(null);
    setVariantOrigin(null);
    setError(null);
  }, []);

  const switchDraft = useCallback(
    (id: string) => {
      if (id === currentDraftId) return;
      const store = readDraftsStore() ?? { current: id, order: [], drafts: {} };
      store.current = id;
      // Drop tabs for empty never-saved drafts the user is leaving behind.
      setDraftTabs((tabs) => tabs.filter((t) => t.id === id || store.drafts[t.id]));
      writeDraftsStore(store);
      setCurrentDraftId(id);
      applyDraftState(store.drafts[id] ?? null);
      setNotice(null);
    },
    [currentDraftId, applyDraftState],
  );

  const commitRename = useCallback(
    (id: string, raw: string) => {
      setRenamingId(null);
      const label = raw.trim();
      if (!label) return;
      const store = readDraftsStore();
      if (store?.drafts[id]) {
        store.drafts[id].label = label;
        writeDraftsStore(store);
      }
      setDraftTabs((tabs) => tabs.map((t) => (t.id === id ? { ...t, label } : t)));
      if (id === currentDraftId) setDraftName(label);
    },
    [currentDraftId],
  );

  const newDraft = useCallback(() => {
    const id = crypto.randomUUID();
    const store = readDraftsStore();
    if (store) {
      store.current = id;
      writeDraftsStore(store);
    }
    setDraftTabs((tabs) => [...tabs.filter((t) => (readDraftsStore()?.drafts ?? {})[t.id]), { id, label: 'new draft' }]);
    setCurrentDraftId(id);
    applyDraftState(null);
    setNotice(null);
  }, [applyDraftState]);

  const deleteDraft = useCallback(
    (id: string) => {
      const tab = draftTabs.find((t) => t.id === id);
      if (!window.confirm(`Delete draft “${tab?.label ?? 'untitled'}”? Library saves are untouched.`)) return;
      const store = readDraftsStore();
      if (store) {
        delete store.drafts[id];
        store.order = store.order.filter((x) => x !== id);
        writeDraftsStore(store);
      }
      const remaining = draftTabs.filter((t) => t.id !== id);
      setDraftTabs(remaining);
      if (id === currentDraftId) {
        const nextId = remaining[0]?.id;
        if (nextId) {
          const st = readDraftsStore();
          if (st) {
            st.current = nextId;
            writeDraftsStore(st);
          }
          setCurrentDraftId(nextId);
          applyDraftState(readDraftsStore()?.drafts[nextId] ?? null);
        } else {
          newDraft();
        }
      }
    },
    [draftTabs, currentDraftId, applyDraftState, newDraft],
  );

  // ---- bootstrap: cities, library, usage, draft restore ----
  useEffect(() => {
    let mounted = true;
    void fetchRatedCities(200).then((rows) => {
      if (!mounted) return;
      setCities(rows);
      if (rows.length > 0) setCityId((prev) => prev || rows[0].id);
    });
    void supabase.rpc('admin_list_studio_assets').then(async ({ data }) => {
      // Studio-tab drafts (prompt "studio-draft:*") store JSON, not HTML — they live in the
      // Library tab, not here.
      if (!mounted || !Array.isArray(data)) return;
      const rows = (data as SavedAsset[]).filter((a) => !a.prompt?.startsWith('studio-draft:'));
      setAssets(rows);
    });
    void supabase.rpc('admin_studio_usage').then(({ data }) => {
      if (mounted && data && typeof data === 'object') {
        const d = data as { month_cost_usd?: number; mine_cost_usd?: number };
        setUsage({
          month_cost_usd: Number(d.month_cost_usd ?? 0),
          monthly_cap_usd: 10,
          mine_cost_usd: typeof d.mine_cost_usd === 'number' ? d.mine_cost_usd : undefined,
        });
      }
    });
    try {
      let store = readDraftsStore();
      if (!store) {
        // Migrate the legacy single draft once.
        const raw = window.localStorage.getItem(DRAFT_KEY);
        if (raw) {
          const legacy = JSON.parse(raw) as MakeDraftState;
          if (legacy.html) {
            const id = crypto.randomUUID();
            store = { current: id, order: [id], drafts: { [id]: legacy } };
            writeDraftsStore(store);
            window.localStorage.removeItem(DRAFT_KEY);
          }
        }
      }
      if (store && store.order.length > 0) {
        // Every session OPENS ON A FRESH ASSET (Evan 2026-10-28: "default here should be new
        // asset") — past work is not lost: it sits in the Drafts panel on the right, one click
        // away. currentDraftId is already a brand-new UUID, so the first generation autosaves
        // into its own new tab.
        setDraftTabs(store.order.map((id) => ({ id, label: draftLabelOf(store!.drafts[id]) })));
      }
    } catch {
      /* no draft */
    }
    return () => {
      mounted = false;
    };
    // applyDraftState is stable (empty deps); listed to satisfy the lint without re-running.
  }, [applyDraftState]);

  // ---- draft autosave: the current slot in the multi-draft store ----
  useEffect(() => {
    if (!html) return;
    const snapshot: MakeDraftState = {
      prompt,
      html,
      width,
      height,
      assetId,
      at: Date.now(),
      versions: versions.slice(-3),
      instructions,
      usedAnswers,
      photoOptions,
      venueOptions,
      backdrops,
      exportLabel,
      label: draftName.trim() || undefined,
      manualEdits: manualEditsRef.current,
    };
    const store = readDraftsStore() ?? { current: currentDraftId, order: [], drafts: {} };
    store.current = currentDraftId;
    if (!store.order.includes(currentDraftId)) store.order.push(currentDraftId);
    store.drafts[currentDraftId] = snapshot;
    // Cap the slots; the oldest unlabeled ones go first (library is the durable save).
    while (store.order.length > 12) {
      const drop = store.order.find((id) => id !== currentDraftId) ?? store.order[0];
      store.order = store.order.filter((x) => x !== drop);
      delete store.drafts[drop];
    }
    writeDraftsStore(store);
    const label = draftLabelOf(snapshot);
    setDraftTabs((tabs) => {
      const i = tabs.findIndex((t) => t.id === currentDraftId);
      if (i < 0) return [...tabs, { id: currentDraftId, label }];
      if (tabs[i].label === label) return tabs;
      const copy = tabs.slice();
      copy[i] = { id: currentDraftId, label };
      return copy;
    });
  }, [html, prompt, width, height, assetId, versions, instructions, usedAnswers, photoOptions, venueOptions, backdrops, exportLabel, draftName, currentDraftId]);

  const discardDraft = useCallback(() => {
    const store = readDraftsStore();
    if (store) {
      delete store.drafts[currentDraftId];
      store.order = store.order.filter((x) => x !== currentDraftId);
      writeDraftsStore(store);
    }
    setDraftTabs((tabs) => tabs.filter((t) => t.id !== currentDraftId));
    applyDraftState(null);
    setNotice(null);
  }, [currentDraftId, applyDraftState]);

  // ---- rich context: rankings, venue detail, approved photos, app stats ----
  // `ov` (overrides) exists because entity resolution and generation run in ONE flow tick:
  // the setState calls in resolveEntities have not flushed yet when buildContext runs, so the
  // resolved values are passed straight through instead of read back out of state.
  type ResolvedEntities = {
    city?: GrowthCity | null;
    venue?: GrowthVenue | null;
    extraVenues?: GrowthVenue[];
    campus?: string | null;
  };
  const buildContext = useCallback(async (ov?: ResolvedEntities): Promise<unknown | undefined> => {
    const parts: Record<string, unknown> = {};
    const photoSet = new Set<string>();
    const optionGroups: { venue: string; urls: string[] }[] = [];
    const venueOpts: VenueOpt[] = [];
    const addVenueOpt = (o: VenueOpt) => {
      if (!venueOpts.some((x) => x.name === o.name)) venueOpts.push(o);
    };

    const effCity = ov?.city ?? (attachCity && cityId ? cities.find((c) => c.id === cityId) ?? null : null);
    const effVenue = ov?.venue ?? (attachVenue ? venue : null);
    const effExtra = ov?.extraVenues ?? extraVenues;
    const effCampus = ov?.campus ?? campusName;

    if (effCity) {
      const city = effCity;
      const cityId2 = city.id;
      // Quality floor (Evan 2026-10-09: "minimum should be like 10"): feature venues with
      // at least 10 raters; small cities fall back to the raw top so lists never go empty.
      const topRaw = await fetchCityRankedVenues(cityId2, 50);
      const topFloored = topRaw.filter((v) => (v.venue_shado_unique_users ?? 0) >= 10);
      const top = (topFloored.length >= 3 ? topFloored : topRaw).slice(0, 10);
      let heroByVenue = new Map<string, string>();
      if (attachPhotos && top.length > 0) {
        const { data } = await supabase
          .from('venues')
          .select('id, approved_venue_photo_url')
          .in('id', top.map((v) => v.id))
          .not('approved_venue_photo_url', 'is', null);
        heroByVenue = new Map(
          ((data ?? []) as { id: string; approved_venue_photo_url: string }[]).map((r) => [
            r.id,
            r.approved_venue_photo_url,
          ]),
        );
      }
      for (const v of top) {
        addVenueOpt({
          name: venueDisplayName(v),
          score: v.venue_shado_score,
          raters: v.venue_shado_unique_users,
          photo: heroByVenue.get(v.id) ?? null,
          city: city.name ?? null,
          cityId: city.id,
          id: v.id,
        });
      }
      const cityHeroUrls = top
        .map((v) => heroByVenue.get(v.id))
        .filter((u): u is string => Boolean(u));
      if (cityHeroUrls.length > 1) {
        optionGroups.push({ venue: `${city.name ?? 'City'} — top venues`, urls: cityHeroUrls });
      }
      parts.city = {
        name: city?.name,
        total_ratings: city?.total_shado_ratings,
        top_venues: top.map((v, i) => {
          const photo = heroByVenue.get(v.id);
          if (photo) photoSet.add(photo);
          return {
            rank: i + 1,
            name: venueDisplayName(v),
            score: v.venue_shado_score,
            raters: v.venue_shado_unique_users,
            ...(photo ? { photo } : {}),
          };
        }),
      };
    }

    if (effVenue) {
      const venue = effVenue;
      const ctx = await fetchVenueRankContext(venue);
      let detail: Record<string, unknown> = {};
      const { data: extra } = await supabase
        .from('venues')
        .select(
          'approved_venue_photo_url, community_price, community_peak_time_overall, community_peak_time_weekend, mood_tags_aggregated',
        )
        .eq('id', venue.id)
        .maybeSingle();
      if (extra) {
        const e = extra as {
          approved_venue_photo_url: string | null;
          community_price: unknown;
          community_peak_time_overall: unknown;
          community_peak_time_weekend: unknown;
          mood_tags_aggregated: unknown;
        };
        detail = {
          ...(e.community_price != null ? { price: e.community_price } : {}),
          ...(e.community_peak_time_overall != null ? { peak_time: e.community_peak_time_overall } : {}),
          ...(e.community_peak_time_weekend != null ? { peak_time_weekend: e.community_peak_time_weekend } : {}),
          ...(e.mood_tags_aggregated != null ? { mood_tags: e.mood_tags_aggregated } : {}),
        };
        if (attachPhotos && e.approved_venue_photo_url) photoSet.add(e.approved_venue_photo_url);
      }
      let photos: string[] = [];
      if (attachPhotos) {
        const { data: rows } = await supabase
          .from('venue_photos')
          .select('public_url')
          .eq('venue_id', venue.id)
          .eq('approved', true)
          .order('display_order', { ascending: true })
          .limit(8);
        photos = ((rows ?? []) as { public_url: string | null }[])
          .map((r) => r.public_url)
          .filter((u): u is string => Boolean(u));
        photos.forEach((u) => photoSet.add(u));
      }
      {
        const hero = (extra as { approved_venue_photo_url: string | null } | null)?.approved_venue_photo_url ?? null;
        const urls = Array.from(new Set([...(hero ? [hero] : []), ...photos]));
        if (urls.length > 0) optionGroups.push({ venue: venueDisplayName(venue), urls });
        addVenueOpt({
          name: venueDisplayName(venue),
          score: venue.venue_shado_score,
          raters: venue.venue_shado_unique_users,
          photo: urls[0] ?? null,
          city: ctx.cityName ?? null,
          id: venue.id,
        });
      }
      parts.venue = {
        name: venueDisplayName(venue),
        city: ctx.cityName,
        rank: ctx.rank,
        of_rated_in_city: ctx.totalRatedInCity,
        score: venue.venue_shado_score,
        raters: venue.venue_shado_unique_users,
        ratings: venue.venue_shado_rating_count,
        ...detail,
        ...(photos.length > 0 ? { photos } : {}),
      };
    }

    // Venues the brief named beyond the first (head-to-heads, multi-venue carousels) — same
    // treatment as the primary venue: rank context plus the approved hero photo only.
    if (effExtra.length > 0) {
      const extra: Record<string, unknown>[] = [];
      for (const v of effExtra) {
        const ctx = await fetchVenueRankContext(v);
        let photo: string | null = null;
        if (attachPhotos) {
          const { data } = await supabase
            .from('venues')
            .select('approved_venue_photo_url')
            .eq('id', v.id)
            .maybeSingle();
          photo = (data as { approved_venue_photo_url: string | null } | null)?.approved_venue_photo_url ?? null;
          if (photo) photoSet.add(photo);
        }
        extra.push({
          name: venueDisplayName(v),
          city: ctx.cityName,
          rank: ctx.rank,
          of_rated_in_city: ctx.totalRatedInCity,
          score: v.venue_shado_score,
          raters: v.venue_shado_unique_users,
          ...(photo ? { photo } : {}),
        });
        if (photo) optionGroups.push({ venue: venueDisplayName(v), urls: [photo] });
        addVenueOpt({
          name: venueDisplayName(v),
          score: v.venue_shado_score,
          raters: v.venue_shado_unique_users,
          photo,
          city: ctx.cityName ?? null,
          id: v.id,
        });
      }
      parts.more_venues = extra;
    }

    if (effCampus) {
      const { count } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .ilike('university', `%${effCampus}%`);
      parts.campus = { name: effCampus, ...(count != null && count > 0 ? { students_on_shado: count } : {}) };
    }

    // ALWAYS attached (2026-10-10 — "i don't see the photo options" was this pool hiding
    // whenever anything resolved): the best approved photos in the database, labeled with
    // their real venue names and scores, floored at 10 raters.
    {
      const { data: pool } = await supabase
        .from('venues')
        .select('id, venue_name_user_input, venue_shado_score, venue_shado_unique_users, approved_venue_photo_url, city_id')
        .not('approved_venue_photo_url', 'is', null)
        .gte('venue_shado_unique_users', 10)
        .order('venue_shado_score', { ascending: false })
        .limit(12);
      const rows = (pool ?? []) as {
        id: string;
        venue_name_user_input: string | null;
        venue_shado_score: number | null;
        venue_shado_unique_users: number | null;
        approved_venue_photo_url: string;
        city_id: string | null;
      }[];
      const poolCityIds = Array.from(new Set(rows.map((r) => r.city_id).filter((x): x is string => Boolean(x))));
      const poolCityName = new Map<string, string>();
      if (poolCityIds.length > 0) {
        const { data: cs } = await supabase.from('cities').select('id, name').in('id', poolCityIds);
        for (const cRow of (cs ?? []) as { id: string; name: string | null }[]) {
          if (cRow.name) poolCityName.set(cRow.id, cRow.name);
        }
      }
      if (rows.length > 0) {
        parts.photo_pool = rows.map((r) => {
          photoSet.add(r.approved_venue_photo_url);
          // raters INCLUDED — the model invented "12 Shado Ratings" when the pool lacked them.
          return {
            name: r.venue_name_user_input,
            score: r.venue_shado_score,
            raters: r.venue_shado_unique_users,
            photo: r.approved_venue_photo_url,
          };
        });
        for (const r of rows) {
          if (r.venue_name_user_input) {
            addVenueOpt({
              name: r.venue_name_user_input,
              score: r.venue_shado_score,
              raters: r.venue_shado_unique_users,
              photo: r.approved_venue_photo_url,
              city: r.city_id ? poolCityName.get(r.city_id) ?? null : null,
              cityId: r.city_id,
              id: r.id,
            });
          }
        }
        optionGroups.push({
          venue: 'Best approved photos',
          urls: rows.map((r) => r.approved_venue_photo_url),
        });
      }
    }

    if (attachStats) {
      const countOf = async (q: PromiseLike<{ count: number | null; error: unknown }>) => {
        try {
          const { count, error: err } = await q;
          return err ? null : count ?? 0;
        } catch {
          return null;
        }
      };
      const [users, ratings, ratedCities] = await Promise.all([
        countOf(supabase.from('profiles').select('id', { count: 'exact', head: true })),
        countOf(supabase.from('logs').select('id', { count: 'exact', head: true }).is('retired_at', null)),
        countOf(supabase.from('cities').select('id', { count: 'exact', head: true }).gt('total_shado_ratings', 0)),
      ]);
      parts.app_stats = { total_users: users, total_ratings: ratings, cities_with_ratings: ratedCities };
    }

    // Community data — top users, stamp holders, users per city (Evan 2026-10-16: the studio
    // should know "top users, who has what stamps, who's in what city"). Private accounts are
    // excluded from anything name-shaped; the edge prompt carries the usage rules.
    try {
      const [sh, profRes] = await Promise.all([
        loadStampHolders(),
        supabase.from('profiles').select('city'),
      ]);
      const everyone = new Map<string, { username: string | null; isPrivate: boolean | null; venuesRated: number; citiesRated: number; city: string | null; university: string | null }>();
      for (const list of Object.values(sh.holdersByStampId)) {
        for (const h of list) if (!everyone.has(h.id)) everyone.set(h.id, h);
      }
      const publicUsers = Array.from(everyone.values()).filter((h) => h.isPrivate !== true && h.username);
      const topUsers = publicUsers
        .sort((x, y) => y.venuesRated - x.venuesRated)
        .slice(0, 10)
        .map((h) => ({
          username: h.username,
          venues_rated: h.venuesRated,
          cities_rated: h.citiesRated,
          city: h.city,
          university: h.university,
        }));
      const cityCounts = new Map<string, number>();
      for (const r of ((profRes.data ?? []) as { city: string | null }[])) {
        const ct = (r.city ?? '').trim();
        if (!ct) continue;
        cityCounts.set(ct, (cityCounts.get(ct) ?? 0) + 1);
      }
      parts.community = {
        raters_total: sh.ratersEvaluated,
        top_users: topUsers,
        stamps: STAMP_DEFINITIONS.map((d) => {
          const holders = (sh.holdersByStampId[d.id] ?? []).filter((h) => h.isPrivate !== true && h.username);
          return {
            stamp: `${d.emoji} ${d.title}`,
            holders: (sh.holdersByStampId[d.id] ?? []).length,
            top_holders: holders.slice(0, 3).map((h) => h.username),
          };
        }),
        users_by_home_city: Array.from(cityCounts.entries())
          .sort((x, y) => y[1] - x[1])
          .slice(0, 8)
          .map(([ct, n]) => ({ city: ct, users: n })),
      };
    } catch {
      /* community block is best-effort — a failed read never blocks a generation */
    }

    if (backdrops.length > 0) {
      const textures = backdrops.filter((b2) => b2.kind !== 'image');
      const freeform = backdrops.filter((b2) => b2.kind === 'image');
      if (textures.length > 0) parts.ai_backdrops = textures.map((b2) => b2.url);
      if (freeform.length > 0) {
        parts.ai_generated_images = {
          note: 'AI-generated images Evan made for this design — usable anywhere (hero art, cutouts, illustration). NOT photos of real venues or people; never caption them as one.',
          images: freeform.map((b2) => ({ url: b2.url, prompt: b2.prompt })),
        };
      }
      backdrops.forEach((b2) => photoSet.add(b2.url));
    }

    if (photoSet.size > 0) parts.photo_urls_allowed = Array.from(photoSet);
    setPhotoOptions(optionGroups);
    setVenueOptions(venueOpts);
    return Object.keys(parts).length > 0 ? parts : undefined;
  }, [attachCity, cityId, cities, attachVenue, venue, extraVenues, campusName, backdrops]);

  /**
   * Turn the names the plan call pulled out of the brief into live attachments: city names
   * match the rated-cities list, venue names go through searchRatedVenues, a campus becomes
   * a context block with its student count. This is what makes "Brown Jug story with its
   * score" work with zero checkboxes — the brief itself is the data request.
   */
  const resolveEntities = useCallback(
    async (ents: PlanEntities | undefined): Promise<ResolvedEntities> => {
      if (!ents) return {};
      const chips: string[] = [];
      const resolved: ResolvedEntities = {};

      const cityName = (ents.cities ?? [])[0];
      if (cityName) {
        const needle = cityName.toLowerCase();
        const hit =
          cities.find((c) => (c.name ?? '').toLowerCase() === needle) ??
          cities.find((c) => (c.name ?? '').toLowerCase().includes(needle));
        if (hit) {
          resolved.city = hit;
          setAttachCity(true);
          setCityId(hit.id);
          chips.push(`${hit.name} — city top 10`);
        }
      }

      const found: GrowthVenue[] = [];
      for (const vn of (ents.venues ?? []).slice(0, 4)) {
        try {
          const rows = await searchRatedVenues(vn, 3);
          const best = rows.find((r) => venueDisplayName(r).toLowerCase() === vn.toLowerCase()) ?? rows[0];
          if (best && !found.some((f) => f.id === best.id)) found.push(best);
        } catch {
          /* an unmatched name just stays text in the brief */
        }
      }
      if (found.length > 0) {
        resolved.venue = found[0];
        resolved.extraVenues = found.slice(1);
        setAttachVenue(true);
        setVenue(found[0]);
        setExtraVenues(found.slice(1));
        chips.push(...found.map((v) => `${venueDisplayName(v)} — score, rank, photos`));
      }

      const campus = (ents.campuses ?? [])[0];
      if (campus) {
        resolved.campus = campus;
        setCampusName(campus);
        chips.push(`${campus} — campus`);
      }

      setResolvedChips(chips);
      return resolved;
    },
    [cities],
  );

  const applyUsage = useCallback((u?: UsageMeter & { cost_usd: number }) => {
    if (u) setUsage({ month_cost_usd: u.month_cost_usd, monthly_cap_usd: u.monthly_cap_usd });
  }, []);

  // ---- generation flow: plan (questions) → generate ----
  const answersForApi = useCallback((): { question: string; answer: string }[] => {
    if (!questions) return [];
    return questions
      .filter((q) => answers[q.id]?.trim())
      .map((q) => ({ question: q.question, answer: answers[q.id].trim() }));
  }, [questions, answers]);

  /**
   * The strips used to know only what attached BEFORE generating — so a venue the model picked
   * out of the pool (Carnegie Club, 2026-10-10) had no photo set and no alternatives. This maps
   * the FINISHED asset's photo URLs back to their venues in the DB, pulls each one's full
   * approved photo set + real stats, and adds same-city alternatives to the venue switcher.
   * Best-effort and silent: enrichment failing never breaks the asset.
   */
  const enrichFromAsset = useCallback(async (fullHtml: string) => {
    try {
      const urls = Array.from(
        new Set(
          fullHtml.match(/https:\/\/[a-z0-9]+\.supabase\.co\/storage\/v1\/object\/public\/[^"'\s)]+/gi) ?? [],
        ),
      );
      if (urls.length === 0) return;
      const [byHero, byPhoto] = await Promise.all([
        supabase.from('venues').select('id').in('approved_venue_photo_url', urls),
        supabase.from('venue_photos').select('venue_id').in('public_url', urls),
      ]);
      const ids = Array.from(
        new Set([
          ...(((byHero.data ?? []) as { id: string }[]).map((r) => r.id)),
          ...(((byPhoto.data ?? []) as { venue_id: string }[]).map((r) => r.venue_id)),
        ]),
      ).slice(0, 10);
      if (ids.length === 0) return;
      const [{ data: vrows }, { data: prows }] = await Promise.all([
        supabase
          .from('venues')
          .select('id, venue_name_user_input, venue_shado_score, venue_shado_unique_users, city_id, approved_venue_photo_url')
          .in('id', ids),
        supabase
          .from('venue_photos')
          .select('venue_id, public_url, rejected')
          .in('venue_id', ids)
          .eq('approved', true)
          .order('display_order', { ascending: true }),
      ]);
      const venuesFound = (vrows ?? []) as {
        id: string;
        venue_name_user_input: string | null;
        venue_shado_score: number | null;
        venue_shado_unique_users: number | null;
        city_id: string | null;
        approved_venue_photo_url: string | null;
      }[];
      const photosByVenue = new Map<string, string[]>();
      for (const r of (prows ?? []) as { venue_id: string; public_url: string | null; rejected: boolean | null }[]) {
        if (r.rejected === true || !r.public_url) continue;
        const list = photosByVenue.get(r.venue_id) ?? [];
        if (list.length < 8) list.push(r.public_url);
        photosByVenue.set(r.venue_id, list);
      }
      const enrichCityIds = Array.from(
        new Set(venuesFound.map((v) => v.city_id).filter((x): x is string => Boolean(x))),
      );
      const cityNameById = new Map<string, string>();
      if (enrichCityIds.length > 0) {
        const { data: cs } = await supabase.from('cities').select('id, name').in('id', enrichCityIds);
        for (const cRow of (cs ?? []) as { id: string; name: string | null }[]) {
          if (cRow.name) cityNameById.set(cRow.id, cRow.name);
        }
      }
      const newGroups: { venue: string; urls: string[] }[] = [];
      const newOpts: VenueOpt[] = [];
      for (const v of venuesFound) {
        const name = v.venue_name_user_input?.trim();
        if (!name) continue;
        const set = Array.from(
          new Set([...(v.approved_venue_photo_url ? [v.approved_venue_photo_url] : []), ...(photosByVenue.get(v.id) ?? [])]),
        );
        if (set.length > 0) newGroups.push({ venue: name, urls: set });
        newOpts.push({
          name,
          score: v.venue_shado_score,
          raters: v.venue_shado_unique_users,
          photo: set[0] ?? null,
          city: v.city_id ? cityNameById.get(v.city_id) ?? null : null,
          cityId: v.city_id,
          id: v.id,
        });
      }
      // Same-city alternatives so the switcher always has somewhere to go.
      const cityIds = Array.from(new Set(venuesFound.map((v) => v.city_id).filter((c): c is string => Boolean(c)))).slice(0, 2);
      for (const cid of cityIds) {
        const topRaw = await fetchCityRankedVenues(cid, 50);
        const floored = topRaw.filter((v) => (v.venue_shado_unique_users ?? 0) >= 10);
        const top = (floored.length >= 3 ? floored : topRaw).slice(0, 10);
        if (top.length === 0) continue;
        const { data: heroRows } = await supabase
          .from('venues')
          .select('id, approved_venue_photo_url')
          .in('id', top.map((t) => t.id))
          .not('approved_venue_photo_url', 'is', null);
        const hm = new Map(
          ((heroRows ?? []) as { id: string; approved_venue_photo_url: string }[]).map((r) => [r.id, r.approved_venue_photo_url]),
        );
        for (const t of top) {
          newOpts.push({
            name: venueDisplayName(t),
            score: t.venue_shado_score,
            raters: t.venue_shado_unique_users,
            photo: hm.get(t.id) ?? null,
            city: cityNameById.get(cid) ?? null,
            cityId: cid,
            id: t.id,
          });
        }
      }
      setPhotoOptions((prev) => {
        const merged = prev.slice();
        for (const g of newGroups) {
          const i = merged.findIndex((x) => x.venue === g.venue);
          if (i >= 0) merged[i] = { venue: g.venue, urls: Array.from(new Set([...merged[i].urls, ...g.urls])) };
          else merged.unshift(g);
        }
        return merged;
      });
      setVenueOptions((prev) => {
        // New data WINS: a stale persisted entry (pre-cityId builds) must not block the
        // corrected one — that is exactly what put New Orleans venues on a New York slide.
        const merged = prev.slice();
        for (const o of newOpts) {
          const i = merged.findIndex((x) => x.name === o.name);
          if (i >= 0) merged[i] = { ...merged[i], ...o };
          else merged.push(o);
        }
        return merged;
      });
    } catch {
      /* enrichment is best-effort */
    }
  }, []);

  const runGenerate = useCallback(
    async (
      withAnswers: { question: string; answer: string }[],
      ov?: ResolvedEntities,
      nVariants?: number,
    ) => {
      setBusy('generate');
      setError(null);
      setNotice(null);
      setVariantChoices(null);
      const context = await buildContext(ov);
      const res = await callStudio({
        prompt: prompt.trim(),
        width,
        height,
        context,
        ...(slideCount > 1 ? { slides: slideCount } : {}),
        ...(nVariants && nVariants > 1 && slideCount <= 1 ? { variants: nVariants } : {}),
        answers: withAnswers.length > 0 ? withAnswers : undefined,
      });
      setBusy(null);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      if (!res.data.html) {
        setError(res.data.error ?? 'Generation returned nothing');
        return;
      }
      applyUsage(res.data.usage);
      if (res.data.truncated) {
        setNotice('The asset ran long and may be cut off — simplify the brief and regenerate.');
      }
      setQuestions(null);
      if (VARIANT_MARKER.test(res.data.html)) {
        const opts = res.data.html.split(VARIANT_SPLIT).map((s) => s.trim()).filter(Boolean);
        if (opts.length > 1) {
          setVariantChoices(opts);
          setVariantOrigin({ kind: 'generate', answers: withAnswers });
          return; // nothing is "the asset" until Evan picks one
        }
      }
      setHtml(res.data.html);
      setSlideIdx(0);
      setAssetId(null);
      setVersions([{ html: res.data.html, at: Date.now(), note: 'Generated from brief' }]);
      setVersionIdx(0);
      setInstructions([]);
      manualEditsRef.current = []; // a fresh generation starts a fresh manual-edit ledger
      setUsedAnswers(withAnswers);
      void enrichFromAsset(res.data.html);
    },
    [prompt, width, height, slideCount, buildContext, applyUsage, enrichFromAsset],
  );

  /** Make a picked option the working asset, with the bookkeeping its origin implies. */
  const pickVariant = useCallback(
    (idx: number) => {
      const choice = variantChoices?.[idx];
      if (!choice || !variantOrigin) return;
      const picked = ensureStylesheet(choice);
      setHtml(picked);
      setSlideIdx(0);
      if (variantOrigin.kind === 'generate') {
        setAssetId(null);
        setVersions([{ html: picked, at: Date.now(), note: `Generated — picked option ${idx + 1}` }]);
        setVersionIdx(0);
        setInstructions([]);
        setUsedAnswers(variantOrigin.answers);
      } else {
        setInstructions((list) => [...list, variantOrigin.instruction]);
        setVersions((vs) => {
          const next = [
            ...vs.slice(-5),
            { html: picked, at: Date.now(), note: `${variantOrigin.instruction} — option ${idx + 1}` },
          ];
          setVersionIdx(next.length - 1);
          return next;
        });
      }
      setVariantChoices(null);
      setVariantOrigin(null);
      void enrichFromAsset(picked);
    },
    [variantChoices, variantOrigin, enrichFromAsset],
  );

  /**
   * One flow for every brief: the plan call ALWAYS runs first, because it is also the entity
   * extractor — "top 5 Ann Arbor bars" has to become live city data before generation whether
   * or not Evan wants clarifying questions. With Ask-first off the questions are simply
   * discarded; the ~2¢ plan call is what buys zero-checkbox data attachment.
   */
  const startFlow = useCallback(async () => {
    if (!prompt.trim() || busy) return;
    setBusy('plan');
    setError(null);
    setNotice(null);
    setResolvedChips([]);
    const context = await buildContext();
    const res = await callStudio({ mode: 'plan', prompt: prompt.trim(), width, height, context });
    if (!res.ok) {
      setBusy(null);
      setError(res.error);
      return;
    }
    applyUsage(res.data.usage);
    const resolved = await resolveEntities(res.data.entities);
    setBusy(null);
    const qs = askFirst ? res.data.questions ?? [] : [];
    if (qs.length === 0) {
      await runGenerate(askFirst ? [] : answersForApi(), resolved, variantsFromText(prompt));
      return;
    }
    setQuestions(qs);
    setAnswers({});
  }, [prompt, busy, askFirst, runGenerate, answersForApi, resolveEntities, buildContext, width, height, applyUsage]);

  const slides = useMemo(() => (html ? splitSlides(html) : []), [html]);

  useEffect(() => {
    setSlideIdx((i) => Math.min(i, Math.max(0, slides.length - 1)));
  }, [slides.length]);

  // A restored draft or library asset gets its strips rebuilt from the asset itself once.
  const enrichedOnceRef = useRef(false);
  useEffect(() => {
    if (!html || enrichedOnceRef.current) return;
    enrichedOnceRef.current = true;
    void enrichFromAsset(html);
  }, [html, enrichFromAsset]);

  /**
   * The full asset with any in-preview text edits folded back in. The iframe only ever holds
   * ONE slide, so its body replaces that slide and the others join back around it. The font
   * <link> is stripped from the capture and re-attached to slide 1 only, keeping the stored
   * document in the exact shape the edge function emits.
   */
  const currentHtml = useCallback((): string | null => {
    const doc = frameRef.current?.contentDocument;
    const edited = doc?.body && doc.body.innerHTML.trim() ? doc.body.innerHTML : null;
    if (!edited) return html;
    const cleaned = edited
      .replace(/<link[^>]*clash-fonts\.css[^>]*>\s*/gi, '')
      .replace(/<style[^>]*data-mk-doc-base[^>]*>[\s\S]*?<\/style>\s*/gi, '')
      .replace(/<style[^>]*data-mk-ui[^>]*>[\s\S]*?<\/style>\s*/gi, '')
      .replace(/\s*data-mk-sel="[^"]*"/gi, '')
      .trim();
    if (slides.length <= 1) return `${STYLESHEET_LINE}\n${cleaned}`;
    const next = slides.slice();
    const idx = Math.min(slideIdx, next.length - 1);
    next[idx] = idx === 0 ? `${STYLESHEET_LINE}\n${cleaned}` : cleaned;
    return next.join('\n<!--SLIDE-->\n');
  }, [html, slides, slideIdx]);

  const runRefine = useCallback(
    async (nVariants?: number) => {
      const base = currentHtml();
      if (!base || !refine.trim() || busy) return;
      const instruction = refine.trim();
      // "show me options for the headline" typed into refine works like the 3-options button.
      const askedVariants = nVariants ?? variantsFromText(instruction);
      setBusy('refine');
      setError(null);
      setNotice(null);
      setVariantChoices(null);
      const context = await buildContext();
      const res = await callStudio({
        html: base,
        instruction,
        width,
        height,
        context,
        ...(slides.length > 1 ? { slides: slides.length } : {}),
        ...(askedVariants && askedVariants > 1 && slides.length <= 1 ? { variants: askedVariants } : {}),
        prompt: prompt.trim(),
        answers: usedAnswers.length > 0 ? usedAnswers : undefined,
        prior_instructions: historyWithManualEdits(instructions),
      });
      setBusy(null);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      if (!res.data.html) {
        setError(res.data.error ?? 'Refine returned nothing');
        return;
      }
      applyUsage(res.data.usage);
      if (VARIANT_MARKER.test(res.data.html)) {
        const opts = res.data.html.split(VARIANT_SPLIT).map((s) => s.trim()).filter(Boolean);
        if (opts.length > 1) {
          setRefine('');
          setVariantChoices(opts);
          setVariantOrigin({ kind: 'refine', instruction });
          return;
        }
      }
      setHtml(res.data.html);
      setRefine('');
      setInstructions((list) => [...list, instruction]);
      setVersions((vs) => {
        const next = [...vs.slice(-5), { html: res.data.html!, at: Date.now(), note: instruction }];
        setVersionIdx(next.length - 1);
        return next;
      });
      void enrichFromAsset(res.data.html);
    },
    [currentHtml, refine, busy, width, height, slides, buildContext, applyUsage, prompt, usedAnswers, instructions, enrichFromAsset, historyWithManualEdits],
  );

  /** The text a viewer actually READS on the slide — tags, styles and URLs stripped. Name
   * matching ran against raw markup until 2026-10-26, so venue names hiding in photo URLs,
   * class names and old alt text pulled whole photo strips onto designs that never mention a
   * venue (Evan: "only give me photo options if it makes sense for what I'm making"). */
  const slideVisibleText = useCallback((cur: string): string => {
    return cur
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }, []);

  /** The venue the viewed slide features, detected loosely (CAPS, optional leading "The"). */
  const currentSlideVenue = useMemo(() => {
    const cur = slides.length > 1 ? slides[Math.min(slideIdx, slides.length - 1)] ?? '' : html ?? '';
    const curText = slideVisibleText(cur);
    // Search the registry AND every loaded city list — a venue picked from a city list on an
    // older build may exist nowhere else.
    const haystack = [...venueOptions, ...Object.values(cityVenueLists).flat()];
    return (
      haystack.find((v) => {
        const n = v.name.toLowerCase();
        return curText.includes(n) || curText.includes(n.replace(/^the\s+/, ''));
      }) ?? null
    );
  }, [slides, slideIdx, html, venueOptions, cityVenueLists, slideVisibleText]);

  /** The CITY the viewed slide belongs to: the detected venue's city, else a rated city whose
   * name appears in the slide text. No city → no venue swaps (never a mixed-city list). */
  /** Every rated city the viewed slide mentions. One = a venue slide's city; several = a
   * cover/teaser listing cities (no venue controls there). */
  const slideCityMatches = useMemo((): { id: string; name: string }[] => {
    const cur = slides.length > 1 ? slides[Math.min(slideIdx, slides.length - 1)] ?? '' : html ?? '';
    const curText = slideVisibleText(cur);
    // "New York, USA" must match "New York City" — try the full name and the name without a
    // trailing "City".
    return cities
      .filter((c) => {
        if (!c.name) return false;
        const full = c.name.toLowerCase();
        const short = full.replace(/\s+city$/, '');
        return (full.length >= 4 && curText.includes(full)) || (short.length >= 4 && curText.includes(short));
      })
      .map((c) => ({ id: c.id, name: c.name ?? '' }));
  }, [slides, slideIdx, html, cities, slideVisibleText]);

  const currentSlideCity = useMemo((): { id: string; name: string } | null => {
    // The city WRITTEN ON THE SLIDE outranks the venue's DB city: The Green Mill's row lives
    // in Evanston, but the slide says Chicago — the design's claim is what Evan is editing.
    if (slideCityMatches.length === 1) return slideCityMatches[0];
    if (currentSlideVenue?.cityId) {
      return { id: currentSlideVenue.cityId, name: currentSlideVenue.city ?? '' };
    }
    if (slideCityMatches.length >= 2 && currentSlideVenue) return slideCityMatches[0];
    return null;
  }, [slideCityMatches, currentSlideVenue]);

  // The slide switcher's REAL list (Evan 2026-10-10: "literally just be NYC venues sorted most
  // to least rated that have photos approved from users"): fetched live for the slide's
  // city — approved user photo required, most raters first — cached per city.
  useEffect(() => {
    const cid = currentSlideCity?.id;
    if (!cid || cityVenueLists[cid]) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('venues')
        .select('id, venue_name_user_input, venue_shado_score, venue_shado_unique_users, approved_venue_photo_url')
        .eq('city_id', cid)
        .not('approved_venue_photo_url', 'is', null)
        .gt('venue_shado_rating_count', 0)
        .order('venue_shado_unique_users', { ascending: false })
        // Every qualifying venue in the city (Evan 2026-10-16: "all of the most rated and top
        // rated that have a user-submitted photo approved as hero") — the biggest city has 64
        // hero venues, so 100 means no city is ever truncated.
        .limit(100);
      if (cancelled) return;
      const rows = ((data ?? []) as {
        id: string;
        venue_name_user_input: string | null;
        venue_shado_score: number | null;
        venue_shado_unique_users: number | null;
        approved_venue_photo_url: string;
      }[])
        .filter((r) => r.venue_name_user_input)
        .map((r) => ({
          name: r.venue_name_user_input as string,
          score: r.venue_shado_score,
          raters: r.venue_shado_unique_users,
          photo: r.approved_venue_photo_url,
          city: currentSlideCity?.name ?? null,
          cityId: cid,
          id: r.id,
          photos: 1, // the hero; gallery counts land just below
        }));
      // Photo counts per venue: hero + approved gallery shots, deduped.
      if (rows.length > 0) {
        const { data: pc } = await supabase
          .from('venue_photos')
          .select('venue_id, public_url, rejected')
          .in('venue_id', rows.map((r) => r.id as string))
          .eq('approved', true);
        if (cancelled) return;
        const byVenue = new Map<string, Set<string>>();
        for (const r of (pc ?? []) as { venue_id: string; public_url: string | null; rejected: boolean | null }[]) {
          if (r.rejected === true || !r.public_url) continue;
          const set = byVenue.get(r.venue_id) ?? new Set<string>();
          set.add(r.public_url);
          byVenue.set(r.venue_id, set);
        }
        for (const row of rows) {
          const set = byVenue.get(row.id as string) ?? new Set<string>();
          if (row.photo) set.add(row.photo);
          row.photos = set.size;
        }
      }
      setCityVenueLists((m) => ({ ...m, [cid]: rows }));
    })();
    return () => {
      cancelled = true;
    };
  }, [currentSlideCity, cityVenueLists]);

  // The slide's venue always gets its own photo group, even when it arrived via an old swap
  // or a hand edit — fetched once per venue, merged into the strip.
  useEffect(() => {
    const v = currentSlideVenue;
    if (!v?.id || photoOptions.some((g) => g.venue === v.name)) return;
    const vid = v.id;
    const vname = v.name;
    const hero = v.photo;
    let cancelled = false;
    void (async () => {
      try {
        const { data: ph } = await supabase
          .from('venue_photos')
          .select('public_url, rejected')
          .eq('venue_id', vid)
          .eq('approved', true)
          .order('display_order', { ascending: true })
          .limit(8);
        if (cancelled) return;
        const urls = Array.from(
          new Set([
            ...(hero ? [hero] : []),
            ...((ph ?? []) as { public_url: string | null; rejected: boolean | null }[])
              .filter((r) => r.rejected !== true && r.public_url)
              .map((r) => r.public_url as string),
          ]),
        );
        if (urls.length > 0) {
          setPhotoOptions((prev) =>
            prev.some((g) => g.venue === vname) ? prev : [{ venue: vname, urls }, ...prev],
          );
        }
      } catch {
        /* best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentSlideVenue, photoOptions]);

  /** STRICT per-slide photo strip (Evan 2026-10-11: "they should only be photo options that
   * apply to the venue that is selected / was chosen by the AI"): the slide's venue's groups
   * only. No venue on the slide → just the pool (for adding imagery to covers), never the
   * whole grab-bag. */
  /** Whether the viewed slide actually renders imagery — the strip exists to SWAP photos, so a
   * photo-free design (milestone graphics, stat cards, pure-type flyers) offers none at all. */
  const slideUsesImagery = useMemo(() => {
    const cur = slides.length > 1 ? slides[Math.min(slideIdx, slides.length - 1)] ?? '' : html ?? '';
    return /<img[\s>]/i.test(cur) || /background(?:-image)?\s*:[^;"']*url\(/i.test(cur);
  }, [slides, slideIdx, html]);

  const visiblePhotoGroups = useMemo(() => {
    if (photoOptions.length === 0) return [];
    // A slide that renders no imagery at all (map/stat/pure-type designs) offers NO photo
    // swaps, whatever the markup or venue names still carry (Evan 2026-10-28: "dont show
    // photo options unless relevant... they are not here on this one").
    if (!slideUsesImagery) return [];
    const cur = slides.length > 1 ? slides[Math.min(slideIdx, slides.length - 1)] ?? '' : html ?? '';
    const curText = slideVisibleText(cur);
    const POOL = 'Best approved photos';
    const matched = photoOptions.filter((g) => {
      if (g.venue === POOL) return false;
      if (currentSlideVenue && g.venue === currentSlideVenue.name) return true;
      if (g.urls.some((u) => cur.includes(u))) return true;
      // Name match against VISIBLE text only — raw markup carries venue names inside photo
      // URLs and stale alts, which is how a milestone graphic got five venues' galleries.
      const name = g.venue.split(' — ')[0].trim().toLowerCase();
      return name.length >= 3 && curText.includes(name);
    });
    if (matched.length > 0) return matched;
    // A carousel slide that names no venue (cover, CTA, city teasers) gets NOTHING — the pool
    // only backs single assets, where imagery has nowhere else to come from. And only assets
    // that already RENDER imagery: a text-only design has nothing a photo would swap into.
    if (!currentSlideVenue && slides.length <= 1 && slideUsesImagery) {
      const pool = photoOptions.find((g) => g.venue === POOL);
      return pool ? [pool] : [];
    }
    return [];
  }, [photoOptions, slides, slideIdx, html, currentSlideVenue, slideVisibleText, slideUsesImagery]);


  /** Instant per-slide venue change. Works on the slide's TEXT NODES via DOMParser, because
   * designs split names across elements ("THE" / "CARNEGIE CLUB" on separate lines) and a
   * contiguous string replace never matches those. The matched words are replaced across
   * nodes (markup stays balanced), casing is mirrored, score and rater counts are rewritten,
   * and the photo swaps at string level. Free, no AI call, logged in history. */
  const swapVenue = useCallback(
    (cur: VenueOpt,
     next: VenueOpt) => {
      const full = currentHtml();
      if (!full || cur.name === next.name) return;
      const sl = splitSlides(full);
      const multi = sl.length > 1;
      const idx = multi ? Math.min(slideIdx, sl.length - 1) : 0;
      let target = multi ? sl[idx] : full;

      // Photo first — pure string level.
      const curPhotos = photoOptions.find((g) => g.venue === cur.name)?.urls ?? (cur.photo ? [cur.photo] : []);
      if (next.photo && !target.includes(next.photo)) {
        let swappedPhoto = false;
        for (const u of curPhotos) {
          if (target.includes(u)) {
            target = target.split(u).join(next.photo);
            swappedPhoto = true;
            break;
          }
        }
        if (!swappedPhoto) {
          // Whatever storage photo the slide carries gets replaced — the pick must always land.
          const any = target.match(/https:\/\/[a-z0-9]+\.supabase\.co\/storage\/v1\/object\/public\/[^"'\s)]+/i);
          if (any) target = target.split(any[0]).join(next.photo);
        }
      }

      const doc = new DOMParser().parseFromString(target, 'text/html');
      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
      const nodes: Text[] = [];
      let tn: Node | null;
      while ((tn = walker.nextNode())) nodes.push(tn as Text);
      // \u0001 separates nodes so words in adjacent nodes cannot jam together, while the
      // flexible separator still lets a name span the boundary.
      const joined = nodes.map((n) => n.data).join('\u0001');
      const flexSep = '[\\s\\u0001]+';
      const nameToRe = (nm: string) => nm.trim().split(/\s+/).map(escapeRe).join(flexSep);
      const candidates = cur.name ? [cur.name, cur.name.replace(/^the\s+/i, '')] : [];
      let match: RegExpExecArray | null = null;
      let usedName = '';
      for (const nm of candidates) {
        if (!nm) continue;
        const re = new RegExp(nameToRe(nm), 'i');
        match = re.exec(joined);
        if (match) {
          usedName = nm;
          break;
        }
      }
      if (!match) {
        // Evan 2026-10-16: a pick must ALWAYS auto-populate, never drop instructions into the
        // Refine box. When the current venue's name is unknown or absent, replace the slide's
        // HEADING structurally: the heading is the all-caps text group (the city line is mixed
        // case, numbers are the score) — take the parent whose caps nodes total the most text.
        const capsByParent = new Map<Node | null, Text[]>();
        for (const n of nodes) {
          const t = n.data.trim();
          if (t.length < 3) continue;
          if (!/[A-Z]/.test(t) || t !== t.toUpperCase()) continue;
          if (/^[\d.,\s%#]+$/.test(t)) continue;
          if (/RATINGS|RATERS|SWIPE|SHADO/.test(t) && t.length < 16) continue;
          const list = capsByParent.get(n.parentNode) ?? [];
          list.push(n);
          capsByParent.set(n.parentNode, list);
        }
        let best: Text[] | null = null;
        let bestLen = 0;
        capsByParent.forEach((list) => {
          const len = list.reduce((acc, n) => acc + n.data.trim().length, 0);
          if (len > bestLen) {
            bestLen = len;
            best = list;
          }
        });
        if (best && bestLen >= 4) {
          const headingNodes: Text[] = best;
          headingNodes.forEach((n, i) => {
            n.data = i === 0 ? next.name.toUpperCase() : '';
          });
        }
        // Standalone score node (the big digits) → the new score.
        if (next.score != null) {
          for (const n of nodes) {
            const t = n.data.trim();
            if (/^\d{1,3}(?:\.\d)?$/.test(t)) {
              // Whole numbers, always (Evan 2026-10-16: "it should not" revert to decimals) —
              // his standing no-decimals instruction beats mirroring a stray decimal the old
              // refine path planted on a slide.
              n.data = n.data.replace(t, String(Math.round(next.score)));
              break;
            }
          }
        }
      }
      if (match) {
        // eslint-disable-next-line no-control-regex -- \u0001 is our own node separator
        const sample = match[0].replace(/\u0001/g, ' ');
        let replName = usedName.toLowerCase() === cur.name.toLowerCase().replace(/^the\s+/, '')
          ? next.name.replace(/^The\s+/i, '')
          : next.name;
        if (usedName.toLowerCase() === cur.name.toLowerCase()) replName = next.name;
        if (sample === sample.toUpperCase()) replName = replName.toUpperCase();
        const mStart = match.index;
        const mEnd = match.index + match[0].length;
        let pos = 0;
        let placed = false;
        for (const n of nodes) {
          const nStart = pos;
          const nEnd = pos + n.data.length;
          pos = nEnd + 1; // the separator
          if (nEnd <= mStart || nStart >= mEnd) continue;
          const from = Math.max(0, mStart - nStart);
          const to = Math.min(n.data.length, mEnd - nStart);
          n.data = n.data.slice(0, from) + (placed ? '' : replName) + n.data.slice(to);
          placed = true;
        }
      }

      for (const n of nodes) {
        if (cur.score != null && next.score != null) {
          const one = cur.score.toFixed(1);
          if (n.data.includes(one)) {
            n.data = n.data.split(one).join(String(Math.round(next.score)));
          } else {
            const whole = String(Math.round(cur.score));
            // Only a node that is essentially JUST the score (the big violet digits) — and it
            // stays a whole number: the slide's format wins over the one-decimal brand default.
            if (n.data.trim() === whole) {
              n.data = n.data.replace(whole, String(Math.round(next.score)));
            }
          }
        }
        if (next.raters != null) {
          n.data = n.data.replace(/\d+(\s*(?:shado\s*)?(?:ratings|raters|students))/gi, `${next.raters}$1`);
        }
      }

      target = doc.body.innerHTML;
      let nextFull: string;
      if (multi) {
        const copy = sl.slice();
        copy[idx] = target;
        nextFull = copy.join('\n<!--SLIDE-->\n');
      } else {
        nextFull = target;
      }
      setHtml(nextFull);
      setVersions((vs) => {
        const nv = [...vs.slice(-5), { html: nextFull, at: Date.now(), note: `Venue swap — ${next.name}` }];
        setVersionIdx(nv.length - 1);
        return nv;
      });
      // The swapped-in venue must become a first-class citizen or everything downstream dies:
      // detection (and with it the dropdown) needs it in venueOptions, and the strip needs its
      // real photo set instead of falling back to noise.
      setVenueOptions((prev) => {
        const i = prev.findIndex((x) => x.name === next.name);
        if (i >= 0) {
          const copy = prev.slice();
          copy[i] = { ...copy[i], ...next };
          return copy;
        }
        return [...prev, next];
      });
      if (next.id) {
        const vid = next.id;
        void (async () => {
          try {
            const { data: ph } = await supabase
              .from('venue_photos')
              .select('public_url, rejected')
              .eq('venue_id', vid)
              .eq('approved', true)
              .order('display_order', { ascending: true })
              .limit(8);
            const urls = Array.from(
              new Set([
                ...(next.photo ? [next.photo] : []),
                ...((ph ?? []) as { public_url: string | null; rejected: boolean | null }[])
                  .filter((r) => r.rejected !== true && r.public_url)
                  .map((r) => r.public_url as string),
              ]),
            );
            if (urls.length > 0) {
              setPhotoOptions((prev) => {
                const i = prev.findIndex((g) => g.venue === next.name);
                if (i >= 0) {
                  const copy = prev.slice();
                  copy[i] = { venue: next.name, urls: Array.from(new Set([...copy[i].urls, ...urls])) };
                  return copy;
                }
                return [{ venue: next.name, urls }, ...prev];
              });
            }
          } catch {
            /* best-effort */
          }
        })();
      }
    },
    [currentHtml, slideIdx, photoOptions],
  );

  /** Tap a photo under the output: if this slide already uses one of that venue's photos it
   * swaps INSTANTLY (string replace, free, undoable via history); otherwise it pre-fills the
   * Refine box so adding the photo is one explicit Apply (that one costs a refine). */
  const swapPhoto = useCallback(
    (group: { venue: string; urls: string[] }, url: string) => {
      const full = currentHtml();
      if (!full) return;
      const sl = splitSlides(full);
      const multi = sl.length > 1;
      const idx = multi ? Math.min(slideIdx, sl.length - 1) : 0;
      const target = multi ? sl[idx] : full;
      if (target.includes(url)) {
        setNotice('That photo is already on this slide.');
        return;
      }
      let used = group.urls.find((u) => u !== url && target.includes(u));
      if (!used) {
        // Mismatched slides happen (old swaps changed text but not imagery): any storage
        // photo on the slide is fair game to replace with the venue's real one.
        const any = target.match(/https:\/\/[a-z0-9]+\.supabase\.co\/storage\/v1\/object\/public\/[^"'\s)]+/i);
        if (any && any[0] !== url) used = any[0];
      }
      if (!used) {
        setRefine(`Use this exact photo of ${group.venue} as the imagery: ${url}`);
        setNotice('This slide has no photo of that venue to swap — the instruction is in the Refine box, hit Apply.');
        return;
      }
      const swapped = target.split(used).join(url);
      let nextFull: string;
      if (multi) {
        const copy = sl.slice();
        copy[idx] = swapped;
        nextFull = copy.join('\n<!--SLIDE-->\n');
      } else {
        nextFull = swapped;
      }
      setHtml(nextFull);
      setVersions((vs) => {
        const next = [...vs.slice(-5), { html: nextFull, at: Date.now(), note: `Photo swap — ${group.venue}` }];
        setVersionIdx(next.length - 1);
        return next;
      });
    },
    [currentHtml, slideIdx],
  );

  /**
   * Slide count changed AFTER an asset exists (Evan 2026-10-09: "i should be able to
   * regenerate without regenerating from the brief and losing my follow ups"): restructure the
   * CURRENT asset to the new count as a refine — the design language, copy, data and the whole
   * instruction history survive; only the slide structure changes.
   */
  const runRestructure = useCallback(async () => {
    const base = currentHtml();
    if (!base || busy) return;
    const target = slideCount;
    const instruction =
      target > 1
        ? `Restructure into exactly ${target} slides: keep the established design language, copy, data and wordmark treatment; add or merge slides as needed (L6 — cover and CTA stay cover and CTA).`
        : 'Condense this carousel into one single asset, keeping the established design language and data.';
    setBusy('refine');
    setError(null);
    setNotice(null);
    const context = await buildContext();
    const res = await callStudio({
      html: base,
      instruction,
      width,
      height,
      ...(target > 1 ? { slides: target } : {}),
      context,
      prompt: prompt.trim(),
      answers: usedAnswers.length > 0 ? usedAnswers : undefined,
      prior_instructions: historyWithManualEdits(instructions),
    });
    setBusy(null);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    if (!res.data.html) {
      setError(res.data.error ?? 'Restructure returned nothing');
      return;
    }
    setHtml(res.data.html);
    setSlideIdx(0);
    setInstructions((list) => [...list, instruction]);
    setVersions((vs) => {
      const next = [
        ...vs.slice(-5),
        { html: res.data.html!, at: Date.now(), note: target > 1 ? `Restructured to ${target} slides` : 'Condensed to a single asset' },
      ];
      setVersionIdx(next.length - 1);
      return next;
    });
    applyUsage(res.data.usage);
    if (res.data.truncated) {
      setNotice('The set ran long and may be cut off — try fewer slides.');
    }
    void enrichFromAsset(res.data.html);
  }, [currentHtml, busy, slideCount, width, height, buildContext, prompt, usedAnswers, instructions, applyUsage, enrichFromAsset, historyWithManualEdits]);

  const restoreVersion = useCallback(
    (idx: number) => {
      const v = versions[idx];
      if (!v) return;
      setHtml(v.html);
      setVersionIdx(idx);
    },
    [versions],
  );

  // ---- export ----
  const exportRoot = useCallback((): HTMLElement | null => {
    const doc = frameRef.current?.contentDocument;
    return (doc?.querySelector('div') as HTMLElement | null) ?? null;
  }, []);

  const exportPng = useCallback(async () => {
    clearEditSel(); // the dashed selection outline must never reach a PNG
    const root = exportRoot();
    if (!root) return;
    setBusy('export');
    setError(null);
    try {
      const fontCss = await getClashFontCss();
      const dataUrl = await toPng(root, {
        pixelRatio: exportScale,
        cacheBust: false,
        ...(fontCss ? { fontEmbedCSS: fontCss } : {}),
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      const base = slugify(exportLabel.trim() || prompt);
      const tag = exportScale > 1 ? `@${exportScale}x` : '';
      a.download =
        slides.length > 1
          ? `shado-${base}-${width}x${height}${tag}-${slideIdx + 1}of${slides.length}.png`
          : `shado-${base}-${width}x${height}${tag}.png`;
      a.click();
    } catch {
      setError('Export failed — usually a photo/webfont CORS hiccup. Try once more, or remove photos.');
    } finally {
      setBusy(null);
    }
  }, [exportRoot, prompt, exportLabel, exportScale, width, height, slides, slideIdx, clearEditSel]);

  /**
   * Renders one slide in a hidden full-size iframe and returns its PNG. Off-screen (not
   * display:none — html-to-image needs layout), fonts awaited, then the same toPng path the
   * visible export uses, so the two can never disagree.
   */
  const renderSlideToPng = useCallback(
    (slideHtml: string): Promise<string> =>
      new Promise<string>((resolve, reject) => {
        const f = document.createElement('iframe');
        f.setAttribute('sandbox', 'allow-same-origin');
        f.style.cssText = `position:fixed;left:-100000px;top:0;width:${width}px;height:${height}px;border:0;`;
        f.srcdoc = ensureStylesheet(slideHtml);
        f.onload = () => {
          void (async () => {
            try {
              const doc = f.contentDocument;
              if (!doc) throw new Error('no document');
              doc.body.style.margin = '0';
              doc.body.style.background = '#08090A';
              try {
                await (doc as Document & { fonts?: { ready: Promise<unknown> } }).fonts?.ready;
              } catch {
                /* fonts best-effort */
              }
              await new Promise((r) => setTimeout(r, 250));
              const root = doc.querySelector('div') as HTMLElement | null;
              if (!root) throw new Error('no root');
              const fontCss = await getClashFontCss();
              resolve(
                await toPng(root, {
                  pixelRatio: exportScale,
                  cacheBust: false,
                  ...(fontCss ? { fontEmbedCSS: fontCss } : {}),
                }),
              );
            } catch (e) {
              reject(e instanceof Error ? e : new Error('render failed'));
            } finally {
              f.remove();
            }
          })();
        };
        document.body.appendChild(f);
      }),
    [width, height, exportScale],
  );

  /** Flip slides WITHOUT losing in-place text edits: fold the current iframe body back into
   * the stored document first, then move. */
  const flipTo = useCallback(
    (next: number) => {
      const merged = currentHtml();
      if (merged && merged !== html) setHtml(merged);
      setSlideIdx(Math.max(0, Math.min(slides.length - 1, next)));
    },
    [currentHtml, html, slides.length],
  );

  /** The whole carousel as a numbered PNG set — shado-…-1of5.png, 2of5, … */
  const exportAllSlides = useCallback(async () => {
    const full = currentHtml();
    if (!full || busy) return;
    const set = splitSlides(full);
    setBusy('export');
    setError(null);
    try {
      for (let i = 0; i < set.length; i++) {
        const dataUrl = await renderSlideToPng(set[i]);
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `shado-${slugify(exportLabel.trim() || prompt)}-${width}x${height}${exportScale > 1 ? `@${exportScale}x` : ''}-${i + 1}of${set.length}.png`;
        a.click();
        // Chrome needs breathing room between programmatic downloads or it drops some.
        await new Promise((r) => setTimeout(r, 450));
      }
      setNotice(
        `${set.length} slides exported. If only the first one saved, allow multiple downloads for this site when Chrome asks.`,
      );
    } catch {
      setError('Export failed — usually a photo/webfont CORS hiccup. Try once more, or remove photos.');
    } finally {
      setBusy(null);
    }
  }, [currentHtml, busy, renderSlideToPng, prompt, exportLabel, exportScale, width, height]);

  const copyPng = useCallback(async () => {
    clearEditSel();
    const root = exportRoot();
    if (!root || typeof ClipboardItem === 'undefined') return;
    setBusy('export');
    setError(null);
    try {
      const fontCss = await getClashFontCss();
      const blob = await toBlob(root, {
        pixelRatio: exportScale,
        cacheBust: false,
        ...(fontCss ? { fontEmbedCSS: fontCss } : {}),
      });
      if (!blob) throw new Error('no blob');
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setNotice('Copied to clipboard');
    } catch {
      setError('Copy failed — use Export PNG instead.');
    } finally {
      setBusy(null);
    }
  }, [exportRoot, exportScale, clearEditSel]);

  // ---- library ----
  const saveAsset = useCallback(async () => {
    const body = currentHtml();
    if (!body || busy) return;
    setBusy('save');
    setError(null);
    const title = exportLabel.trim() || prompt.trim().slice(0, 80) || 'Untitled';
    const { data, error: err } = await supabase.rpc('admin_save_studio_asset', {
      p_id: assetId,
      p_title: title,
      p_prompt: prompt.trim(),
      p_html: body,
      p_width: width,
      p_height: height,
    });
    setBusy(null);
    if (err) {
      setError(err.message);
      return;
    }
    setAssetId(data as string);
    setNotice('Saved to library');
    const { data: rows } = await supabase.rpc('admin_list_studio_assets');
    if (Array.isArray(rows))
      setAssets((rows as SavedAsset[]).filter((a) => !a.prompt?.startsWith('studio-draft:')));
  }, [currentHtml, busy, prompt, exportLabel, assetId, width, height]);

  const srcDoc = useMemo(() => {
    if (!html) return '';
    if (slides.length <= 1) return ensureStylesheet(html);
    return ensureStylesheet(slides[Math.min(slideIdx, slides.length - 1)] ?? '');
  }, [html, slides, slideIdx]);

  /** A srcDoc change reloads the iframe — the selected element no longer exists. */
  useEffect(() => {
    editTargetRef.current = null;
    setEditSel(null);
  }, [srcDoc]);
  const generateLabel =
    busy === 'plan'
      ? 'Thinking about your brief…'
      : busy === 'generate'
        ? 'Generating… (can take ~30s)'
        : html
          ? 'Regenerate from brief'
          : askFirst
            ? 'Start — it may ask a few questions'
            : 'Generate';

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[430px,minmax(0,1fr),200px]">
      {/* -------- controls column -------- */}
      <div className="min-w-0 space-y-4">
        <div className="admin-card">
          <div className="admin-eyebrow mb-2">Describe the asset</div>
          <textarea
            className="admin-input !h-auto !py-3 resize-y"
            rows={4}
            placeholder="e.g. A game-day flyer: “Where Michigan actually goes out” with the top 5 Ann Arbor bars and their photos, bold Clash type, QR footer"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-mutedDim">Size</span>
            <select
              className="admin-input !w-auto !px-2 !text-[12px]"
              value={preset.label}
              onChange={(e) => {
                const p = PRESETS.find((x) => x.label === e.target.value);
                if (p) {
                  setPreset(p);
                  setCustomW('');
                  setCustomH('');
                }
              }}
            >
              {PRESETS.map((p) => (
                <option key={p.label} value={p.label}>
                  {p.label}
                </option>
              ))}
            </select>
            <span className="flex items-center gap-1 text-[11px] text-mutedDim">
              or custom
              <input
                className="admin-input !w-[64px] !px-1.5 !text-[11px]"
                placeholder="W"
                value={customW}
                onChange={(e) => setCustomW(e.target.value.replace(/\D/g, ''))}
              />
              ×
              <input
                className="admin-input !w-[64px] !px-1.5 !text-[11px]"
                placeholder="H"
                value={customH}
                onChange={(e) => setCustomH(e.target.value.replace(/\D/g, ''))}
              />
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-mutedDim">Slides</span>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <button
                key={n}
                type="button"
                data-active={slideCount === n}
                onClick={() => setSlideCount(n)}
                className="admin-pill"
              >
                {n === 1 ? 'Single' : n}
              </button>
            ))}
            {slideCount > 1 ? (
              <span className="text-[11px] text-mutedDim">carousel — exports as a numbered set{slideCount >= 8 ? " · big sets cost ~50–80¢ and take ~1 min" : ""}</span>
            ) : null}
          </div>

          {/* Small and right under the slide options (Evan 2026-10-28) — not a full-width banner. */}
          <button
            type="button"
            disabled={busy != null || !prompt.trim()}
            onClick={() => void startFlow()}
            className="admin-btn admin-btn-primary admin-btn-sm mt-3"
          >
            {generateLabel}
          </button>

          <div className="mt-3 space-y-2">
            <label className="flex items-center gap-2 text-[12px] text-muted">
              <input type="checkbox" checked={askFirst} onChange={(e) => setAskFirst(e.target.checked)} />
              Ask me clarifying questions before building
            </label>
            <p className="text-[10.5px] leading-relaxed text-mutedDim">
              It helps itself to live data — name a venue, city or campus and its real scores,
              ranks, counts and approved photos attach automatically — plus top users, stamp
              holders and per-city user counts. Say “show me options” (or a number of options)
              anywhere, including in a refine, to pick between directions.
            </p>
          </div>

          {resolvedChips.length > 0 ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.08em] text-mutedDim">From the brief:</span>
              {resolvedChips.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-accent/35 bg-accent/10 px-2 py-0.5 text-[10px] text-accent"
                >
                  {c}
                </span>
              ))}
              <button
                type="button"
                onClick={() => {
                  setResolvedChips([]);
                  setAttachCity(false);
                  setAttachVenue(false);
                  setVenue(null);
                  setExtraVenues([]);
                  setCampusName(null);
                }}
                className="text-[10px] text-mutedDim hover:text-white"
                title="Detach everything resolved from the brief"
              >
                clear
              </button>
            </div>
          ) : null}

          {usage ? (
            <p className="mt-2 text-[11px] tabular-nums text-mutedDim">
              This month, all admins together: ${usage.month_cost_usd.toFixed(2)} of $
              {usage.monthly_cap_usd} cap
              {usage.mine_cost_usd != null ? ` · you ~$${usage.mine_cost_usd.toFixed(2)}` : ''} ·
              questions ~2¢ · assets ~10–20¢
            </p>
          ) : null}
        </div>

        {questions && questions.length > 0 ? (
          <div className="admin-card border-accent/30">
            <div className="admin-eyebrow mb-1 !text-[#b79cff]">Before it builds — quick direction</div>
            <p className="mb-3 text-[11.5px] leading-relaxed text-mutedDim">
              Answer what you care about, skip what you don’t. Tap an option or type your own.
            </p>
            <div className="space-y-4">
              {questions.map((q) => (
                <div key={q.id}>
                  <div className="mb-1.5 text-[12.5px] font-medium text-textPrimary">{q.question}</div>
                  {q.options.length > 0 ? (
                    <div className="mb-1.5 flex flex-wrap gap-1.5">
                      {q.options.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          data-active={answers[q.id] === opt}
                          onClick={() => setAnswers((a) => ({ ...a, [q.id]: a[q.id] === opt ? '' : opt }))}
                          // Long model-written options must WRAP inside the pill — the fixed
                          // 28px height let two-line labels spill across the border.
                          className="admin-pill !h-auto !whitespace-normal !py-1.5 text-left leading-snug"
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <input
                    className="admin-input !text-[12px]"
                    placeholder="…or answer in your own words"
                    value={
                      questions.find((qq) => qq.id === q.id)?.options.includes(answers[q.id] ?? '')
                        ? ''
                        : answers[q.id] ?? ''
                    }
                    onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                disabled={busy != null}
                onClick={() => void runGenerate(answersForApi(), undefined, variantsFromText(prompt))}
                className="admin-btn admin-btn-primary admin-btn-sm"
              >
                Build it
              </button>
              {slideCount <= 1 ? (
                <button
                  type="button"
                  disabled={busy != null}
                  onClick={() => void runGenerate(answersForApi(), undefined, 3)}
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                >
                  Build 3 options
                </button>
              ) : null}
              <button
                type="button"
                disabled={busy != null}
                onClick={() => void runGenerate([], undefined, variantsFromText(prompt))}
                className="admin-btn admin-btn-ghost admin-btn-sm"
              >
                Skip — designer’s choice
              </button>
            </div>
          </div>
        ) : null}

        {html && slideCount !== slides.length && !(slideCount === 1 && slides.length <= 1) ? (
          <div className="admin-card border-accent/30">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[12px] text-muted">
                Slide count changed ({slides.length <= 1 ? 'single' : slides.length} →{' '}
                {slideCount === 1 ? 'single' : slideCount}).
              </span>
              <button
                type="button"
                disabled={busy != null}
                onClick={() => void runRestructure()}
                className="admin-btn admin-btn-primary admin-btn-sm"
              >
                {busy === 'refine' ? 'Working…' : 'Restructure current asset'}
              </button>
            </div>
            <p className="mt-1.5 text-[10.5px] leading-relaxed text-mutedDim">
              Keeps the design, your edits and every refinement — only the slide structure
              changes. “Regenerate from brief” is the one that starts over.
            </p>
          </div>
        ) : null}

        {html ? (
          <div className="admin-card">
            <div className="admin-eyebrow mb-2">Refine</div>
            <div className="flex items-start gap-2">
              {/* Grows like the brief box. Enter sends, Shift+Enter makes a new line. */}
              <textarea
                rows={2}
                className="admin-input flex-1 !h-auto !py-2.5 resize-y !text-[12.5px] leading-relaxed"
                placeholder="e.g. bigger headline, use the photo as the backdrop, more violet — Shift+Enter for a new line"
                value={refine}
                onChange={(e) => setRefine(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void runRefine();
                  }
                }}
              />
              <button
                type="button"
                disabled={busy != null || !refine.trim()}
                onClick={() => void runRefine()}
                className="admin-btn admin-btn-secondary admin-btn-sm"
              >
                {busy === 'refine' ? 'Working…' : 'Apply'}
              </button>
              {slides.length <= 1 ? (
                <button
                  type="button"
                  disabled={busy != null || !refine.trim()}
                  onClick={() => void runRefine(3)}
                  title="Apply the instruction three different ways and pick between them"
                  className="admin-btn admin-btn-secondary admin-btn-sm whitespace-nowrap"
                >
                  3 options
                </button>
              ) : null}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                className="admin-input !w-[220px] !text-[12px]"
                placeholder="draft label — names exports & library saves"
                value={exportLabel}
                onChange={(e) => setExportLabel(e.target.value)}
                title="Downloads become shado-<label>-<size>[-NofM].png and the library save takes this name; blank = named after the brief"
              />
              <span className="flex items-center gap-1 text-[11px] text-mutedDim">
                quality
                {([1, 2, 3] as const).map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-active={exportScale === n}
                    onClick={() => setExportScale(n)}
                    className="admin-pill"
                    title={`${width * n} × ${height * n}px`}
                  >
                    {n}×
                  </button>
                ))}
                <span className="tabular-nums">{width * exportScale}px wide</span>
              </span>
              <button
                type="button"
                disabled={busy != null}
                onClick={() => void exportPng()}
                className="admin-btn admin-btn-primary admin-btn-sm"
              >
                {busy === 'export'
                  ? 'Working…'
                  : slides.length > 1
                    ? `Export slide ${slideIdx + 1}`
                    : `Export PNG ${width}×${height}`}
              </button>
              {slides.length > 1 ? (
                <button
                  type="button"
                  disabled={busy != null}
                  onClick={() => void exportAllSlides()}
                  className="admin-btn admin-btn-primary admin-btn-sm"
                >
                  {busy === 'export' ? 'Working…' : `Export all ${slides.length} slides`}
                </button>
              ) : null}
              <button
                type="button"
                disabled={busy != null}
                onClick={() => void copyPng()}
                className="admin-btn admin-btn-secondary admin-btn-sm"
              >
                Copy PNG
              </button>
              <button
                type="button"
                disabled={busy != null}
                onClick={() => void saveAsset()}
                className="admin-btn admin-btn-secondary admin-btn-sm"
              >
                {busy === 'save' ? 'Saving…' : assetId ? 'Update in library' : 'Save to library'}
              </button>
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-mutedDim">
              Click any text in the preview to edit it directly — edits survive refine, save and
              export. Work in progress autosaves as a draft; the library is the durable save.
            </p>
          </div>
        ) : null}

        {versions.length > 0 ? (
          <div className="admin-card">
            <div className="admin-eyebrow mb-2">Conversation history</div>
            <div className="space-y-1 text-[12px]">
              <div className="px-2 py-1 text-mutedDim">
                <span className="text-muted">Brief:</span> {prompt.trim() || '—'}
              </div>
              {usedAnswers.map((a) => (
                <div key={a.question} className="px-2 py-1 text-mutedDim">
                  <span className="text-muted">{a.question}</span> → {a.answer}
                </div>
              ))}
              {versions.map((v, i) => (
                <button
                  key={`${v.at}-${i}`}
                  type="button"
                  onClick={() => restoreVersion(i)}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-white/[0.03] ${
                    i === versionIdx ? 'bg-accent/10 text-textPrimary' : 'text-muted'
                  }`}
                >
                  <span className="shrink-0 font-semibold tabular-nums text-[#b79cff]">v{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate">{v.note}</span>
                  <span className="shrink-0 text-[10.5px] tabular-nums text-mutedDim">{timeAgo(new Date(v.at).toISOString())}</span>
                </button>
              ))}
            </div>
            <p className="mt-2 text-[10.5px] leading-relaxed text-mutedDim">
              Tap a version to bring it back — refining continues from whichever version is active.
              The model sees the brief, your answers and every prior instruction on each refine.
            </p>
          </div>
        ) : null}

      </div>

      {/* -------- preview column -------- */}
      <div className="min-w-0">
        {error ? <div className="admin-banner-error mb-3">{error}</div> : null}
        {notice ? (
          <div className="mb-3 flex items-center gap-3 rounded-lg border border-borderStrong bg-surfaceElevated px-3 py-2 text-[12px] text-muted">
            <span className="min-w-0 flex-1">{notice}</span>
            {notice.startsWith('Restored') ? (
              <button type="button" onClick={discardDraft} className="admin-btn admin-btn-ghost admin-btn-sm">
                Discard draft
              </button>
            ) : null}
          </div>
        ) : null}
        {busy === 'plan' || busy === 'generate' || busy === 'refine' ? (
          <GeneratingCanvas
            w={Math.round(width * scale)}
            h={Math.round(height * scale)}
            phase={busy}
          />
        ) : null}
        {busy !== 'plan' && busy !== 'generate' && busy !== 'refine' && variantChoices ? (
          <div className="admin-card mb-4">
            <div className="admin-eyebrow mb-1 !text-[#b79cff]">
              Pick a direction — {variantChoices.length} options
            </div>
            <p className="mb-3 text-[11.5px] leading-relaxed text-mutedDim">
              {variantOrigin?.kind === 'refine'
                ? `“${variantOrigin.instruction}”, three different ways. The ones you don't pick are discarded.`
                : 'Three different takes on the brief. Pick one to keep working on; the rest are discarded.'}
            </p>
            <div className="flex flex-wrap gap-4">
              {variantChoices.map((v, i) => {
                const thumbW = 300;
                const thumbScale = thumbW / width;
                return (
                  <div key={i} className="space-y-2">
                    <div
                      className="overflow-hidden rounded-lg border border-borderStrong bg-[#08090A]"
                      style={{ width: thumbW, height: Math.round(height * thumbScale) }}
                    >
                      <iframe
                        title={`Option ${i + 1}`}
                        sandbox="allow-same-origin"
                        srcDoc={ensureStylesheet(v)}
                        className="pointer-events-none"
                        style={{
                          width,
                          height,
                          border: 0,
                          transform: `scale(${thumbScale})`,
                          transformOrigin: 'top left',
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => pickVariant(i)}
                      className="admin-btn admin-btn-primary admin-btn-sm w-full"
                    >
                      Use option {i + 1}
                    </button>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => {
                setVariantChoices(null);
                setVariantOrigin(null);
              }}
              className="mt-3 text-[11px] text-mutedDim hover:text-white"
            >
              None of these — dismiss
            </button>
          </div>
        ) : null}
        {busy === 'plan' || busy === 'generate' || busy === 'refine' ? null : html ? (
          <div className="admin-card inline-block !p-4">
            {slides.length > 1 ? (
              <div className="mb-3 flex items-center gap-2">
                <button
                  type="button"
                  disabled={slideIdx === 0}
                  onClick={() => flipTo(slideIdx - 1)}
                  className="admin-btn admin-btn-secondary admin-btn-sm disabled:opacity-40"
                >
                  ‹
                </button>
                <span className="text-[12px] tabular-nums text-muted">
                  Slide {slideIdx + 1} of {slides.length}
                </span>
                <button
                  type="button"
                  disabled={slideIdx >= slides.length - 1}
                  onClick={() => flipTo(slideIdx + 1)}
                  className="admin-btn admin-btn-secondary admin-btn-sm disabled:opacity-40"
                >
                  ›
                </button>
                <span className="text-[10.5px] text-mutedDim">
                  edits on a slide are kept when you flip — they fold into refine, save and export
                </span>
              </div>
            ) : null}
            <div
              ref={previewShellRef}
              className={
                isFull
                  ? 'fixed inset-0 z-[100] flex flex-col items-center justify-center gap-3 bg-black'
                  : 'relative'
              }
            >
              {isFull ? (
                <div className="flex items-center gap-3">
                  {slides.length > 1 ? (
                    <>
                      <button
                        type="button"
                        disabled={slideIdx === 0}
                        onClick={() => flipTo(slideIdx - 1)}
                        className="admin-btn admin-btn-secondary admin-btn-sm disabled:opacity-40"
                      >
                        ‹
                      </button>
                      <span className="text-[12px] tabular-nums text-muted">
                        Slide {slideIdx + 1} of {slides.length}
                      </span>
                      <button
                        type="button"
                        disabled={slideIdx >= slides.length - 1}
                        onClick={() => flipTo(slideIdx + 1)}
                        className="admin-btn admin-btn-secondary admin-btn-sm disabled:opacity-40"
                      >
                        ›
                      </button>
                    </>
                  ) : null}
                  <span className="text-[11px] text-mutedDim">
                    click any text to restyle it · type on the canvas to rewrite
                  </span>
                  <button type="button" onClick={exitFull} className="admin-btn admin-btn-secondary admin-btn-sm">
                    Exit full screen (Esc)
                  </button>
                </div>
              ) : null}
              <div className="relative">
                <div
                  className="overflow-hidden rounded-lg border border-borderStrong bg-[#08090A]"
                  style={{ width: Math.round(width * effScale), height: Math.round(height * effScale) }}
                >
                  <iframe
                    ref={frameRef}
                    title="Generated asset"
                    sandbox="allow-same-origin"
                    srcDoc={srcDoc}
                    style={{
                      width,
                      height,
                      border: 0,
                      background: '#08090A',
                      transform: `scale(${effScale})`,
                      transformOrigin: 'top left',
                    }}
                    onLoad={() => {
                      const doc = frameRef.current?.contentDocument;
                      if (doc) {
                        doc.designMode = 'on';
                        doc.body.style.margin = '0';
                        doc.body.style.background = '#08090A';
                        doc.addEventListener('click', (e) => pickEditTargetRef.current(e));
                      }
                    }}
                  />
                </div>
                {editSel ? (
                  <div
                    className="absolute z-20 w-[236px] space-y-2.5 rounded-xl border border-borderStrong bg-[#101014]/95 p-3 shadow-2xl backdrop-blur"
                    style={{ left: editSel.left, top: editSel.top }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="admin-eyebrow !mb-0">{editSel.kind === 'img' ? 'Image' : 'Text style'}</span>
                      <button
                        type="button"
                        onClick={clearEditSel}
                        className="text-[12px] font-medium text-[#b79cff] hover:text-white"
                      >
                        Done
                      </button>
                    </div>
                    {editSel.kind === 'text' ? (
                      <>
                        <div className="flex items-center gap-1.5">
                          <span className="w-11 shrink-0 text-[11px] text-mutedDim">Size</span>
                          <button
                            type="button"
                            className="admin-pill !px-2"
                            onClick={() => applyEditStyle({ fontSize: Math.max(6, editSel.fontSize - 2) })}
                          >
                            −
                          </button>
                          <input
                            className="admin-input !w-[52px] !px-1 text-center !text-[12px]"
                            value={editSel.fontSize}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              if (Number.isFinite(n) && n > 0) applyEditStyle({ fontSize: n });
                            }}
                          />
                          <button
                            type="button"
                            className="admin-pill !px-2"
                            onClick={() => applyEditStyle({ fontSize: editSel.fontSize + 2 })}
                          >
                            +
                          </button>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-11 shrink-0 text-[11px] text-mutedDim">Weight</span>
                          <select
                            className="admin-input !w-auto !px-1.5 !text-[12px]"
                            value={editSel.weight}
                            onChange={(e) => applyEditStyle({ weight: parseInt(e.target.value, 10) })}
                          >
                            {Array.from(new Set([300, 400, 500, 600, 700, 800, editSel.weight]))
                              .sort((a, b) => a - b)
                              .map((w) => (
                                <option key={w} value={w}>
                                  {w}
                                </option>
                              ))}
                          </select>
                          <div className="ml-auto flex gap-0.5">
                            {(['left', 'center', 'right'] as const).map((a) => (
                              <button
                                key={a}
                                type="button"
                                data-active={editSel.align === a}
                                onClick={() => applyEditStyle({ align: a })}
                                className="admin-pill !px-2 !text-[10px]"
                                title={`Align ${a}`}
                              >
                                {a === 'left' ? 'L' : a === 'center' ? 'C' : 'R'}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-11 shrink-0 text-[11px] text-mutedDim">Color</span>
                          <input
                            type="color"
                            value={editSel.color}
                            onChange={(e) => applyEditStyle({ color: e.target.value })}
                            className="h-6 w-8 cursor-pointer rounded border border-borderStrong bg-transparent"
                          />
                          {['#FFFFFF', '#9A9AA2', '#8B5CF6', '#b79cff'].map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => applyEditStyle({ color: c })}
                              className="h-5 w-5 rounded-full border border-white/20"
                              style={{ background: c }}
                              title={c}
                            />
                          ))}
                        </div>
                        <p className="text-[10px] leading-snug text-mutedDim">
                          Type straight on the canvas to rewrite the words.
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5">
                          <span className="w-11 shrink-0 text-[11px] text-mutedDim">Corners</span>
                          <input
                            type="range"
                            min={0}
                            max={80}
                            value={editSel.radius}
                            onChange={(e) => applyEditStyle({ radius: parseInt(e.target.value, 10) })}
                            className="flex-1 accent-accent"
                          />
                          <span className="w-8 text-right text-[11px] tabular-nums text-mutedDim">{editSel.radius}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-11 shrink-0 text-[11px] text-mutedDim">Opacity</span>
                          <input
                            type="range"
                            min={15}
                            max={100}
                            value={editSel.opacity}
                            onChange={(e) => applyEditStyle({ opacity: parseInt(e.target.value, 10) })}
                            className="flex-1 accent-accent"
                          />
                          <span className="w-8 text-right text-[11px] tabular-nums text-mutedDim">{editSel.opacity}%</span>
                        </div>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={removeEditTarget}
                      className="text-[11px] text-red-400 hover:text-red-300"
                    >
                      Remove from design
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <p className="text-[10.5px] tabular-nums text-mutedDim">
                {width} × {height}px · shown at {(effScale * 100).toFixed(0)}%
              </p>
              <button
                type="button"
                onClick={enterFull}
                className="admin-btn admin-btn-secondary admin-btn-sm"
                title="Work on the output at full size"
              >
                Full screen
              </button>
            </div>
            <p className="mt-1 text-[10.5px] leading-relaxed text-mutedDim">
              Click any text or photo on the canvas for a style panel; type on the canvas to rewrite.
              Every edit folds into refines, saves and exports automatically.
            </p>
            {/* Photo/venue controls appear only when they make sense for THIS design (Evan
                2026-10-26): a group matched, a venue is genuinely named on the slide, or a
                single-city slide that renders imagery (city-top10 style). Otherwise nothing. */}
            {visiblePhotoGroups.some((g) => g.urls.length > 0) ||
            (currentSlideVenue != null && slideUsesImagery) ||
            (currentSlideCity != null && slideUsesImagery) ? (
              <div className="mt-3 border-t border-border/60 pt-3">
                {(() => {
                  const curVenue = currentSlideVenue;
                  // Covers/teasers mention several cities and no venue — nothing to offer.
                  if (!curVenue && slideCityMatches.length !== 1) return null;
                  if (!currentSlideCity) return null;
                  const cityList = cityVenueLists[currentSlideCity.id];
                  if (cityList === undefined) {
                    return (
                      <p className="mb-3 text-[11px] text-mutedDim">
                        Loading {currentSlideCity.name} venues…
                      </p>
                    );
                  }
                  let list = cityList;
                  if (curVenue && !list.some((v) => v.name === curVenue.name)) {
                    list = [curVenue, ...list];
                  }
                  if (list.length < (curVenue ? 2 : 1)) {
                    return (
                      <p className="mb-3 text-[11px] text-mutedDim">
                        No other {currentSlideCity.name} venues with approved user photos yet — no
                        swaps to offer on this slide.
                      </p>
                    );
                  }
                  return (
                    <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] text-muted">
                      Venue on this slide:
                      <VenueDropdown
                        list={list}
                        value={curVenue?.name ?? null}
                        onPick={(next) =>
                          swapVenue(
                            curVenue ?? { name: '', score: null, raters: null, photo: null },
                            next,
                          )
                        }
                      />
                      <span className="text-mutedDim">
                        {currentSlideCity.name} only · most rated first · approved photos only
                      </span>
                    </div>
                  );
                })()}
                <div className="admin-eyebrow mb-2">Photos — tap one to swap it in</div>
                {visiblePhotoGroups.map((g) => (
                  <div key={g.venue} className="mb-2">
                    <div className="mb-1 text-[11px] text-mutedDim">{g.venue}</div>
                    <div className="flex flex-wrap gap-2">
                      {g.urls.map((u) => {
                        const inUse = Boolean(html?.includes(u));
                        return (
                          <button
                            key={u}
                            type="button"
                            onClick={() => swapPhoto(g, u)}
                            title={inUse ? 'In the design now' : 'Swap this photo in'}
                            className={`overflow-hidden rounded-md border-2 transition-colors ${
                              inUse ? 'border-accent' : 'border-transparent hover:border-border'
                            }`}
                          >
                            <img src={u} alt="" className="h-16 w-16 object-cover" loading="lazy" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <p className="text-[10.5px] leading-relaxed text-mutedDim">
                  Violet ring = currently in the design. Swapping is instant and free when this
                  slide already shows one of that venue&rsquo;s photos; otherwise the tap fills the
                  Refine box for you. On a carousel only the venue featured on THIS slide is
                  offered — flip slides to see each venue&rsquo;s set.
                </p>
              </div>
            ) : null}
          </div>
        ) : (
          <EmptyState
            title="Describe anything and generate it"
            hint="Flyers, banners, table tents, story graphics — built in the Shado design system on live data and your approved venue photos. It asks direction questions first, autosaves drafts, and exports print-ready PNGs."
          />
        )}
      </div>

      {/* -------- drafts column (Evan 2026-10-26: vertical, right of the output, renamable) ---- */}
      <div className="min-w-0 space-y-1.5">
        <div className="admin-eyebrow mb-1">Drafts</div>
        {draftTabs.map((t) => (
          <div
            key={t.id}
            className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[12px] ${
              t.id === currentDraftId ? 'border-accent/70 bg-accent/10' : 'border-border bg-surface'
            }`}
          >
            {renamingId === t.id ? (
              <input
                autoFocus
                defaultValue={t.label}
                className="admin-input !h-6 min-w-0 flex-1 !px-1 !text-[12px]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename(t.id, (e.target as HTMLInputElement).value);
                  if (e.key === 'Escape') setRenamingId(null);
                }}
                onBlur={(e) => commitRename(t.id, e.target.value)}
              />
            ) : (
              <button
                type="button"
                className={`min-w-0 flex-1 truncate text-left ${t.id === currentDraftId ? 'text-white' : 'text-muted hover:text-white'}`}
                title={t.id === currentDraftId ? 'Current draft' : 'Switch to this draft'}
                onClick={() => switchDraft(t.id)}
              >
                {t.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => setRenamingId(t.id)}
              className="text-mutedDim hover:text-white"
              title="Rename"
            >
              ✎
            </button>
            <button
              type="button"
              onClick={() => deleteDraft(t.id)}
              className="text-mutedDim hover:text-red-300"
              title="Delete this draft"
            >
              ✕
            </button>
          </div>
        ))}
        <button type="button" onClick={newDraft} className="admin-pill w-full !justify-center" title="Start a fresh draft">
          + New
        </button>
      </div>
    </div>
  );
}
